import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useCurrentModule } from '../contexts/ModuleContext';
import { Layout } from '../components/Layout';
import { TimePicker12h } from '../components/TimePicker12h';
import { COUNTRIES } from '../lib/countries';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { Upload, X, Loader2, Send, Plus, Trash2, User, Save, Download, FileSpreadsheet, Star, Pencil } from 'lucide-react';
import { Checkbox } from '../components/ui/checkbox';
import { StationeryMultiSelect } from '../components/ui/multi-select-stationery';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Default request types with their role visibility
// Locations dropdown for all request types
const TICKET_LOCATIONS = [
  "Mumbai", "Vankas (U1)", "Malkhet (U2)", "Kolkata", "Delhi",
  "Bangalore", "Chennai", "Pune", "Ahmedabad", "Hyderabad"
];

// Maintenance categories for Repairs & Maintenance
const MAINTENANCE_CATEGORIES = [
  "Electrical", "Plumbing", "IT Equipment", "Furniture",
  "Air Conditioning", "Civil Work", "Housekeeping Issue", "Other"
];

// Visibility is now based on DEPARTMENT:
// "Everyone" = visible to all
// "Human Resources Management" = only for employees in Human Resources Management department
// "Administration" = only for employees in Administration or Administration (Travel) departments
// "HR & Admin" = visible to both HR and Admin departments
const DEFAULT_REQUEST_TYPES_CONFIG = [
  { name: "Stationery Request", role_visibility: ["Everyone"] },
  { name: "Courier (Incoming / Outgoing)", role_visibility: ["Everyone"] },
  { name: "Office Assistant Help", role_visibility: ["Everyone"] },
  { name: "Guest Arrival", role_visibility: ["Everyone"] },
  { name: "Organize an Event", role_visibility: ["Everyone"] },
  { name: "Onboarding", role_visibility: ["Human Resources Management", "Admin Head"] },
  { name: "Insurance Removal Request", role_visibility: ["Human Resources Management", "Admin Head"] },
  { name: "Meeting Room Booking", role_visibility: ["Everyone"] },
  { name: "Promotion Box Request", role_visibility: ["Human Resources Management"] },
  { name: "Desk Decor – Birthday", role_visibility: ["Human Resources Management"] },
  { name: "Insurance Addition Request", role_visibility: ["Human Resources Management"] },
  { name: "Repairs & Maintenance", role_visibility: ["Everyone"] },
  { name: "Other", role_visibility: ["Everyone"] }
];

const PRIORITIES = ["High", "Medium", "Low"];

// Helper function to check if user can see a request type based on department and role
const canUserSeeRequestType = (roleVisibility, userRole, userDepartment) => {
  if (!roleVisibility || roleVisibility.length === 0 || roleVisibility.includes("Everyone")) {
    return true;
  }
  
  // Admin Head and Super Admin can see everything
  if (userRole === "Admin Head" || userRole === "Super Admin") {
    return true;
  }
  
  // Check visibility based on department
  for (const visibility of roleVisibility) {
    if (visibility === "Everyone") return true;
    if (visibility === "Admin Head" && (userRole === "Admin Head" || userRole === "Super Admin")) return true;
    
    // Department-based visibility
    if (visibility === "Human Resources Management") {
      if (userDepartment === "Human Resources Management") return true;
    }
    
    if (visibility === "Administration") {
      if (userDepartment === "Administration" || userDepartment === "Administration (Travel)") return true;
    }
    
    if (visibility === "HR & Admin") {
      if (userDepartment === "Human Resources Management" || 
          userDepartment === "Administration" || 
          userDepartment === "Administration (Travel)") return true;
    }
  }
  
  return false;
};

export default function CreateTicketPage() {
  const navigate = useNavigate();
  const routerLocation = useLocation();
  // Read department/module from query string (?module=Admin|IT)
  const queryParams = new URLSearchParams(routerLocation.search);
  const moduleType = (queryParams.get('module') || 'Admin') === 'IT' ? 'IT' : 'Admin';
  const isITModule = moduleType === 'IT';
  // Locked-type mode: when opened from "Book Room" button on Room Schedule,
  // ?type=Meeting%20Room%20Booking&locked=1 — pre-set and disable the request type selector.
  const lockedType = queryParams.get('locked') === '1' ? (queryParams.get('type') || '') : '';
  const { getAuthHeader, employee } = useAuth();
  // Sync URL ?module=… to global header
  const { setCurrentModule } = useCurrentModule();
  useEffect(() => { setCurrentModule(moduleType); }, [moduleType, setCurrentModule]);
  const userRole = employee?.role || 'Employee';
  const userDepartment = employee?.department || '';
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [customRequestTypes, setCustomRequestTypes] = useState([]);
  const [selectedCustomType, setSelectedCustomType] = useState(null);
  const [customFieldValues, setCustomFieldValues] = useState({});
  const [employeeDepartments, setEmployeeDepartments] = useState([]);
  const [roomConfig, setRoomConfig] = useState({});
  const [roomSchedule, setRoomSchedule] = useState([]);
  const [roomConflict, setRoomConflict] = useState(null);
  const [checkingConflict, setCheckingConflict] = useState(false);
  const [unavailableRooms, setUnavailableRooms] = useState([]);
  const [gradeSumMapping, setGradeSumMapping] = useState({});
  const [unratedTickets, setUnratedTickets] = useState([]);
  const [showRatingBlocker, setShowRatingBlocker] = useState(false);
  // For IT module: cascading Category → Subcategory dropdowns
  const [itCategory, setITCategory] = useState('');
  // Client Factory Visit — visit-location itinerary editor
  const [cfvSeg, setCfvSeg] = useState(null);
  const [cfvEditIndex, setCfvEditIndex] = useState(null);
  
  // For IT Team / IT Head / Super Admin: fetch ALL locations so they can raise
  // tickets on behalf of any site (not just their own location_access).
  const [allLocations, setAllLocations] = useState([]);
  useEffect(() => {
    if (!['IT Team', 'IT Head', 'Super Admin'].includes(userRole)) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API}/location-access/locations`, { headers: getAuthHeader() });
        if (res.ok) {
          const data = await res.json();
          if (!cancelled && Array.isArray(data)) setAllLocations(data);
        }
      } catch (_e) { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [userRole, getAuthHeader]);

  // Filter default request types based on user's role and department
  const visibleDefaultTypes = DEFAULT_REQUEST_TYPES_CONFIG
    .filter(type => canUserSeeRequestType(type.role_visibility, userRole, userDepartment))
    .map(type => type.name);
  
  const [formData, setFormData] = useState({
    request_type: lockedType || (moduleType === 'IT' ? 'IT Request' : ''),
    other_request_type: '',    description: '',
    priority: 'Medium',
    department: employee?.department || '',
    attachments: [],
    // Dynamic fields
    courier_type: '',
    courier_from: '',
    courier_pickup_point: '',
    courier_to: '',
    courier_contents: '',
    guest_name: '',
    guest_company: '',
    guest_purpose: '',
    guest_date: '',
    guest_time: '',
    guest_arrival_date: '',
    guest_departure_date: '',
    guest_food_required: 'No',
    guest_car_parking: 'No',
    guest_vehicle_number: '',
    informed_departments: [],
    // Client Factory Visit — structured
    cfv_brand: '',
    cfv_guest_name: '',
    cfv_company: '',
    cfv_guests_count: '',
    cfv_purpose: '',
    cfv_need_invite: 'No',
    cfv_photography: 'No',
    cfv_videography: 'No',
    cfv_transport: 'No',
    cfv_visitors: [],
    cfv_brands: [],
    cfv_food_required: 'No',
    cfv_meals: [],
    cfv_dietary: '',
    cfv_stay_required: 'No',
    cfv_stay_days: '',
    cfv_giveaway: [],
    cfv_giveaway_other: '',
    cfv_car_parking: 'No',
    cfv_visit_locations: [],
    // CFV — Visit details
    cfv_primary_objective: '',
    cfv_primary_objective_other: '',
    cfv_project_linked: 'No',
    cfv_project_name: '',
    cfv_project_location: '',
    cfv_project_stage: '',
    cfv_industry_segment: '',
    cfv_industry_segment_other: '',
    cfv_area_of_interest: '',
    // CFV — Factory visit requirements
    cfv_areas_covered: '',
    cfv_products_demonstrated: '',
    cfv_lab_testing: 'No',
    cfv_lab_testing_details: '',
    // CFV — Internal participation
    cfv_team_members: '',
    cfv_senior_mgmt: 'No',
    // CFV — Presentation & meeting
    cfv_presentation: 'No',
    cfv_presentation_type: '',
    cfv_conference_room: 'No',
    event_name: '',
    event_date: '',
    event_time: '',
    event_location: '',
    event_attendees: '',
    event_requirements: '',
    event_venue: '',
    event_location_dropdown: '',
    event_gift: 'No',
    event_gift_cost: '',
    event_food_arrangement: 'No',
    event_food_budget: '',
    room_type: '',
    room_location: '',
    room_date: '',
    room_start_time: '',
    room_end_time: '',
    room_attendees: '',
    room_requirements: '',
    room_meeting_category: '',
    // Promotion Box fields
    promo_employee_name: '',
    promo_designation: '',
    promo_department: '',
    promo_effective_date: '',
    promo_special_instructions: '',
    promo_entity: '',
    promo_employees: [], // [{employee_code, employee_name, employee_address, receiver_name, receiver_address, receiver_contact}]
    // Desk Decor - Birthday fields
    birthday_employee_name: '',
    birthday_date: '',
    birthday_time: '',
    birthday_department: '',
    birthday_location: '',
    birthday_employees: [], // [{employee_name, required_by_date, department}]
    // Insurance Addition fields
    insurance_add_employees: [], // [{employee_code, employee_name, department, grade, sum_insured, insurance_required_for, member_name, member_dob}]
    stationery_type: '',
    stationery_selected_items: [], // Array of {name, quantity}
    stationery_description: '',
    stationery_required_by_date: '',
    // HR Induction fields
    induction_employee_name: '',
    induction_employee_id: '',
    induction_designation: '',
    induction_joining_date: '',
    induction_location: '',
    induction_department: '',
    induction_employee_type: '',
    induction_photo: null,
    induction_tshirt_size: '',
    // ID Card section (conditional)
    induction_need_id_card: 'no',
    induction_dob: '',
    induction_date_of_issue: '',
    induction_blood_group: '',
    induction_emergency_number: '',
    induction_masked_aadhaar: '',
    induction_office_address: '',
    // Mediclaim section (conditional)
    induction_need_mediclaim: 'no',
    induction_mediclaim_members: [],
    // Facility Requirements (multiple select)
    induction_facility_visiting_card: false,
    induction_facility_hotel: false,
    induction_facility_transport: false,
    induction_food_required: 'no',
    induction_entity: '',  // Entity field for HR Induction
    // Insurance Removal fields
    insurance_employee_name: '',
    insurance_employee_id: '',
    insurance_exit_date: '',
    insurance_employees: [],  // Multi-employee support: [{name, id, exit_date}]
    // Location for all requests
    ticket_location: '',
    // Repairs & Maintenance
    maintenance_category: '',
    maintenance_other_text: '',
  });
  
  // Mediclaim member template
  const emptyMediclaimMember = {
    full_name: '',
    relation: '',
    dob: '',
    doj: '',
    grade: '',
    sum_insured: ''
  };

  // Add mediclaim member
  const addMediclaimMember = () => {
    setFormData(prev => ({
      ...prev,
      induction_mediclaim_members: [...prev.induction_mediclaim_members, { ...emptyMediclaimMember }]
    }));
  };

  // Remove mediclaim member
  const removeMediclaimMember = (index) => {
    setFormData(prev => ({
      ...prev,
      induction_mediclaim_members: prev.induction_mediclaim_members.filter((_, i) => i !== index)
    }));
  };

  // Update mediclaim member
  const updateMediclaimMember = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      induction_mediclaim_members: prev.induction_mediclaim_members.map((member, i) => {
        if (i !== index) return member;
        const updated = { ...member, [field]: value };
        // Auto-populate sum_insured when grade changes
        if (field === 'grade' && value && gradeSumMapping[value]) {
          updated.sum_insured = String(gradeSumMapping[value]);
        }
        return updated;
      })
    }));
  };

  // Fetch custom request types — uses different endpoint for IT module
  const fetchCustomRequestTypes = useCallback(async () => {
    try {
      const endpoint = isITModule ? `${API}/it-request-types` : `${API}/request-types/active`;
      const res = await fetch(endpoint, {
        headers: getAuthHeader(),
      });
      if (res.ok) {
        const data = await res.json();
        // Normalize IT type entries (category/subcategory) → {name, ...} so existing UI can render them
        if (isITModule) {
          const normalized = data.map(it => ({
            id: it.id,
            name: it.subcategory,
            category: it.category,
            sla_hours: it.sla_hours,
            description_only: true,
            field_config: [],
          }));
          setCustomRequestTypes(normalized);
        } else {
          setCustomRequestTypes(data);
        }
      }
    } catch (error) {
      console.error('Failed to fetch custom request types:', error);
    }
  }, [getAuthHeader, isITModule]);

  useEffect(() => {
    if (employee?.department) {
      setFormData(prev => ({ ...prev, department: employee.department }));
    }
    fetchCustomRequestTypes();
    // Fetch departments for HR Induction dropdown
    const fetchDepartments = async () => {
      try {
        const res = await fetch(`${API}/employees/departments-list`, {
          headers: getAuthHeader(),
          
        });
        if (res.ok) {
          const data = await res.json();
          setEmployeeDepartments(data);
        }
      } catch (error) {
        console.error('Fetch departments error:', error);
      }
    };
    fetchDepartments();
    // Fetch room config
    const fetchRoomConfig = async () => {
      try {
        const res = await fetch(`${API}/rooms/config`, {
          headers: getAuthHeader(),
        });
        if (res.ok) {
          const data = await res.json();
          setRoomConfig(data.config || {});
        }
      } catch (error) {
        console.error('Fetch room config error:', error);
      }
    };
    fetchRoomConfig();
    // Fetch grade-to-sum_insured mapping
    const fetchGradeSumMapping = async () => {
      try {
        const res = await fetch(`${API}/employees/grade-sum-mapping`, {
          headers: getAuthHeader(),
          
        });
        if (res.ok) {
          const data = await res.json();
          setGradeSumMapping(data);
        }
      } catch (error) {
        console.error('Fetch grade-sum mapping error:', error);
      }
    };
    fetchGradeSumMapping();
    // Check for unrated completed tickets — filter by current department module
    // so creating an IT request doesn't surface Admin feedback popups (and vice versa)
    const checkUnrated = async () => {
      // Skip rating check for Admin Head, Super Admin and Admin Team
      if (employee?.role === 'Admin Head' || employee?.role === 'Super Admin' || employee?.role === 'Admin Team') return;
      try {
        const res = await fetch(`${API}/tickets/check-unrated?module=${encodeURIComponent(moduleType)}`, {
          headers: getAuthHeader(),
          
        });
        if (res.ok) {
          const data = await res.json();
          if (data.has_unrated) {
            setUnratedTickets(data.unrated_tickets);
            setShowRatingBlocker(true);
          }
        }
      } catch (error) {
        console.error('Check unrated error:', error);
      }
    };
    checkUnrated();
  }, [employee, fetchCustomRequestTypes, getAuthHeader, moduleType]);

  // Handle request type change - check if it's a custom type
  const handleRequestTypeChange = (value) => {
    setFormData(prev => ({ ...prev, request_type: value }));
    
    // Get all default type names (regardless of visibility) for checking
    const allDefaultTypeNames = DEFAULT_REQUEST_TYPES_CONFIG.map(t => t.name);
    
    // If it's a default request type, don't treat as custom
    if (allDefaultTypeNames.includes(value)) {
      setSelectedCustomType(null);
      setCustomFieldValues({});
      return;
    }
    
    // Check if it's a custom request type
    const customType = customRequestTypes.find(t => t.name === value);
    if (customType) {
      setSelectedCustomType(customType);
      // Initialize custom field values
      const initialValues = {};
      customType.fields?.forEach(field => {
        initialValues[field.name] = field.type === 'checkbox' ? false : '';
      });
      setCustomFieldValues(initialValues);
    } else {
      setSelectedCustomType(null);
      setCustomFieldValues({});
    }
  };

  // Room booking conflict check
  const checkRoomConflict = async (location, room, date, startTime, endTime) => {
    if (!location || !room || !date || !startTime || !endTime) {
      setRoomConflict(null);
      return;
    }
    setCheckingConflict(true);
    try {
      const res = await fetch(`${API}/rooms/check-conflict`, {
        method: 'POST',
        headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
        
        body: JSON.stringify({ location, room, date, start_time: startTime, end_time: endTime })
      });
      if (res.ok) {
        const data = await res.json();
        setRoomConflict(data);
      }
    } catch (error) {
      console.error('Conflict check error:', error);
    } finally {
      setCheckingConflict(false);
    }
  };

  // Fetch room schedule
  const fetchRoomSchedule = async (location, room, date) => {
    if (!location || !room || !date) {
      setRoomSchedule([]);
      return;
    }
    try {
      const res = await fetch(`${API}/rooms/schedule?location=${encodeURIComponent(location)}&room=${encodeURIComponent(room)}&date=${date}`, {
        headers: getAuthHeader(),
        
      });
      if (res.ok) {
        const data = await res.json();
        setRoomSchedule(data);
      }
    } catch (error) {
      console.error('Fetch schedule error:', error);
    }
  };

  // Check which rooms are unavailable for the selected time (using bulk API)
  const checkRoomAvailability = async (location, date, startTime, endTime) => {
    if (!location || !date || !startTime || !endTime) {
      setUnavailableRooms([]);
      return;
    }
    try {
      const res = await fetch(
        `${API}/rooms/availability?location=${encodeURIComponent(location)}&date=${date}&start_time=${startTime}&end_time=${endTime}`,
        { headers: getAuthHeader() }
      );
      if (res.ok) {
        const data = await res.json();
        setUnavailableRooms(data.unavailable_rooms || []);
      }
    } catch (error) {
      console.error('Room availability check error:', error);
    }
  };

  // Get rooms based on location and attendee count
  const getRoomsForSelection = (location, attendees) => {
    const config = roomConfig[location];
    if (!config) return [];
    if (config.all) return config.all;
    const count = parseInt(attendees) || 0;
    if (count <= 4) return config.small || [];
    return config.large || [];
  };

  const handleFileUpload = async (files) => {
    setUploading(true);
    const uploadedUrls = [];

    for (const file of files) {
      try {
        const formDataUpload = new FormData();
        formDataUpload.append('file', file);

        const res = await fetch(`${API}/upload`, {
          method: 'POST',
          headers: getAuthHeader(),
          
          body: formDataUpload
        });

        if (res.ok) {
          const data = await res.json();
          uploadedUrls.push(data.url);
        }
      } catch (error) {
        console.error('Upload error:', error);
        toast.error(`Failed to upload ${file.name}`);
      }
    }

    setFormData(prev => ({
      ...prev,
      attachments: [...prev.attachments, ...uploadedUrls]
    }));
    setUploading(false);
  };

  const removeAttachment = (index) => {
    setFormData(prev => ({
      ...prev,
      attachments: prev.attachments.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e, saveAsDraft = false) => {
    e.preventDefault();
    
    // Block submission if there are unrated completed tickets
    if (!saveAsDraft && showRatingBlocker && unratedTickets.length > 0) {
      toast.error('Please rate your completed tickets before raising a new request.');
      return;
    }
    
    if (!formData.request_type) {
      toast.error('Please select a request type');
      return;
    }
    
    // For drafts, allow saving without full validation
    if (!saveAsDraft) {
      // Stationery Request doesn't need description - validate stationery fields instead
      if (formData.request_type === 'Stationery Request') {
        if (!formData.stationery_type) {
          toast.error('Please select a request type (Replacement/New)');
          return;
        }
        if (!formData.stationery_selected_items || formData.stationery_selected_items.length === 0) {
          toast.error('Please select at least one stationery item');
          return;
        }
      }
      // Description is mandatory ONLY for request types that have no other specific fields
      // i.e., "Other" and "Office Assistant Help" - where description is the primary field
      const descriptionOnlyTypes = ['Other', 'Office Assistant Help'];
      if (descriptionOnlyTypes.includes(formData.request_type) && !formData.description.trim()) {
        toast.error('Please provide a description');
        return;
      }
      
      // Meeting Room Booking conflict check
      if (formData.request_type === 'Meeting Room Booking') {
        if (roomConflict && roomConflict.has_conflict) {
          toast.error('Cannot book - this room has a scheduling conflict. Please choose a different time.');
          return;
        }
      }

      // Client Factory Visit validation
      if (formData.request_type === 'Client Factory Visit') {
        const vis = formData.cfv_visitors || [];
        if (vis.length === 0) { toast.error('Add at least one visitor'); return; }
        if (vis.some(v => !v.name?.trim() || !v.contact?.trim() || !v.email?.trim())) { toast.error('Each visitor needs Name, Contact and Email'); return; }
        if (vis.some(v => !v.company?.trim())) { toast.error('Each visitor needs a Company'); return; }
        if (formData.cfv_primary_objective === 'Other' && !formData.cfv_primary_objective_other?.trim()) { toast.error('Please specify the primary purpose'); return; }
        if (formData.cfv_industry_segment === 'Other' && !formData.cfv_industry_segment_other?.trim()) { toast.error('Please specify the Industry / Segment'); return; }
        if (formData.cfv_food_required === 'Yes' && (formData.cfv_meals || []).length === 0) { toast.error('Select at least one meal'); return; }
        if (formData.cfv_stay_required === 'Yes' && !(parseInt(formData.cfv_stay_days) > 0)) { toast.error('Number of Nights must be greater than 0'); return; }
        if ((formData.cfv_giveaway || []).includes('Others') && !formData.cfv_giveaway_other?.trim()) { toast.error('Please specify the Giveaway'); return; }
        if (!formData.cfv_visit_locations || formData.cfv_visit_locations.length === 0) {
          toast.error('Add at least one Visit Location'); return;
        }
      }
    }

    setLoading(true);

    try {
      // Merge form data with custom field values for custom request types
      let submitData = {
        ...formData,
        module: moduleType,  // 'Admin' or 'IT' — routes to correct workflow on backend
        save_as_draft: saveAsDraft,
        // Include custom field values if using a custom request type
        ...(selectedCustomType && { custom_fields: customFieldValues })
      };

      // Auto-generate description for Stationery Request
      if (formData.request_type === 'Stationery Request') {
        const itemsList = formData.stationery_selected_items
          .map(item => `${item.name} (x${item.quantity})`)
          .join(', ');
        let autoDescription = `Stationery Request (${formData.stationery_type}): ${itemsList}`;
        if (formData.stationery_description) {
          autoDescription += ` | Note: ${formData.stationery_description}`;
        }
        submitData.description = autoDescription || 'Draft';
        // Also include the structured items data
        submitData.stationery_items_list = formData.stationery_selected_items;
      }
      
      // For drafts, keep description as empty if not provided (user will fill it later)
      // No default text for drafts

      const res = await fetch(`${API}/tickets`, {
        method: 'POST',
        headers: {
          ...getAuthHeader(),
          'Content-Type': 'application/json'
        },
        
        body: JSON.stringify(submitData)
      });

      if (res.ok) {
        const ticket = await res.json();
        toast.success(saveAsDraft ? 'Saved as draft!' : 'Ticket created successfully!');
        navigate(`/tickets/${ticket.ticket_id}`);
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to create ticket');
      }
    } catch (error) {
      console.error('Create ticket error:', error);
      toast.error('Failed to create ticket');
    } finally {
      setLoading(false);
    }
  };

  const renderDynamicFields = () => {
    switch (formData.request_type) {
      case 'Stationery Request':
        return (
          <>
            <div className="space-y-2">
              <Label>Request Type *</Label>
              <Select
                value={formData.stationery_type}
                onValueChange={(value) => setFormData(prev => ({ ...prev, stationery_type: value }))}
              >
                <SelectTrigger data-testid="stationery-type-select">
                  <SelectValue placeholder="Select request type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Replacement">Replacement</SelectItem>
                  <SelectItem value="New">New</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Stationery Items *</Label>
              <StationeryMultiSelect
                value={formData.stationery_selected_items}
                onChange={(items) => setFormData(prev => ({ ...prev, stationery_selected_items: items }))}
              />
              <p className="text-xs text-muted-foreground">
                Search and select items. You can specify quantity for each selected item.
              </p>
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={formData.stationery_description}
                onChange={(e) => setFormData(prev => ({ ...prev, stationery_description: e.target.value }))}
                placeholder="Additional details about the request..."
                rows={2}
                data-testid="stationery-description"
              />
            </div>
            <div className="space-y-2">
              <Label>Required by Date</Label>
              <Input
                type="date"
                value={formData.stationery_required_by_date}
                onChange={(e) => setFormData(prev => ({ ...prev, stationery_required_by_date: e.target.value }))}
                data-testid="stationery-required-by-date"
              />
            </div>
          </>
        );

      case 'Courier (Incoming / Outgoing)':
        return (
          <>
            <div className="space-y-2">
              <Label>Courier Type *</Label>
              <Select
                value={formData.courier_type}
                onValueChange={(value) => setFormData(prev => ({ ...prev, courier_type: value }))}
              >
                <SelectTrigger data-testid="courier-type">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Incoming">Incoming</SelectItem>
                  <SelectItem value="Outgoing">Outgoing</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>From Address</Label>
                <Input
                  value={formData.courier_from}
                  onChange={(e) => setFormData(prev => ({ ...prev, courier_from: e.target.value }))}
                  placeholder="Sender name/address"
                  data-testid="courier-from"
                />
              </div>
              <div className="space-y-2">
                <Label>Pickup Point</Label>
                <Input
                  value={formData.courier_pickup_point}
                  onChange={(e) => setFormData(prev => ({ ...prev, courier_pickup_point: e.target.value }))}
                  placeholder="Enter pickup location"
                  data-testid="courier-pickup-point"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>To Address</Label>
              <Input
                value={formData.courier_to}
                onChange={(e) => setFormData(prev => ({ ...prev, courier_to: e.target.value }))}
                placeholder="Recipient name/address"
                data-testid="courier-to"
              />
            </div>
            <div className="space-y-2">
              <Label>Contents</Label>
              <Input
                value={formData.courier_contents}
                onChange={(e) => setFormData(prev => ({ ...prev, courier_contents: e.target.value }))}
                placeholder="Brief description of contents"
                data-testid="courier-contents"
              />
            </div>
          </>
        );

      case 'Guest Arrival':
        return (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Guest Name *</Label>
                <Input
                  value={formData.guest_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, guest_name: e.target.value }))}
                  placeholder="Full name of guest"
                  data-testid="guest-name"
                />
              </div>
              <div className="space-y-2">
                <Label>Company</Label>
                <Input
                  value={formData.guest_company}
                  onChange={(e) => setFormData(prev => ({ ...prev, guest_company: e.target.value }))}
                  placeholder="Company/Organization"
                  data-testid="guest-company"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Purpose of Visit</Label>
              <Input
                value={formData.guest_purpose}
                onChange={(e) => setFormData(prev => ({ ...prev, guest_purpose: e.target.value }))}
                placeholder="Meeting, interview, delivery, etc."
                data-testid="guest-purpose"
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Arrival Date *</Label>
                <Input
                  type="date"
                  value={formData.guest_arrival_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, guest_arrival_date: e.target.value }))}
                  data-testid="guest-arrival-date"
                />
              </div>
              <div className="space-y-2">
                <Label>Departure Date</Label>
                <Input
                  type="date"
                  value={formData.guest_departure_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, guest_departure_date: e.target.value }))}
                  data-testid="guest-departure-date"
                />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Expected Time</Label>
                <TimePicker12h
                  value={formData.guest_time}
                  onChange={(val) => setFormData(prev => ({ ...prev, guest_time: val }))}
                  data-testid="guest-time"
                />
              </div>
              <div className="space-y-2">
                <Label>Food Required</Label>
                <Select
                  value={formData.guest_food_required}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, guest_food_required: value }))}
                >
                  <SelectTrigger data-testid="guest-food-required">
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="No">No</SelectItem>
                    <SelectItem value="Yes">Yes</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Car Parking Required</Label>
              <Select
                value={formData.guest_car_parking}
                onValueChange={(value) => setFormData(prev => ({ ...prev, guest_car_parking: value }))}
              >
                <SelectTrigger data-testid="guest-car-parking">
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="No">No</SelectItem>
                  <SelectItem value="Yes">Yes</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {formData.guest_car_parking === 'Yes' && (
              <div className="space-y-2">
                <Label>Vehicle Number</Label>
                <Input
                  value={formData.guest_vehicle_number || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, guest_vehicle_number: e.target.value }))}
                  placeholder="e.g. MH01AB1234"
                  data-testid="guest-vehicle-number"
                />
              </div>
            )}
          </>
        );

      case 'Organize an Event':
        return (
          <>
            <div className="space-y-2">
              <Label>Event Name *</Label>
              <Input
                value={formData.event_name}
                onChange={(e) => setFormData(prev => ({ ...prev, event_name: e.target.value }))}
                placeholder="Name/title of the event"
                data-testid="event-name"
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Date *</Label>
                <Input type="date" value={formData.event_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, event_date: e.target.value }))}
                  data-testid="event-date" />
              </div>
              <div className="space-y-2">
                <Label>Time</Label>
                <TimePicker12h value={formData.event_time}
                  onChange={(val) => setFormData(prev => ({ ...prev, event_time: val }))}
                  data-testid="event-time" />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Venue</Label>
                <Input value={formData.event_venue || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, event_venue: e.target.value }))}
                  placeholder="Venue name" data-testid="event-venue" />
              </div>
              <div className="space-y-2">
                <Label>Location</Label>
                <Select value={formData.event_location_dropdown || ''}
                  onValueChange={(v) => setFormData(prev => ({ ...prev, event_location_dropdown: v }))}>
                  <SelectTrigger data-testid="event-location-dropdown"><SelectValue placeholder="Select location" /></SelectTrigger>
                  <SelectContent>
                    {["Mumbai", "Vankas (U1)", "Malkhet (U2)", "Kolkata", "Delhi", "Bangalore", "Chennai", "Pune", "Ahmedabad", "Hyderabad"].map((loc) => (
                      <SelectItem key={loc} value={loc}>{loc}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Expected Attendees</Label>
              <Input type="number" value={formData.event_attendees}
                onChange={(e) => setFormData(prev => ({ ...prev, event_attendees: e.target.value }))}
                placeholder="Number of attendees" data-testid="event-attendees" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Gift</Label>
                <Select value={formData.event_gift || 'No'}
                  onValueChange={(v) => setFormData(prev => ({ ...prev, event_gift: v }))}>
                  <SelectTrigger data-testid="event-gift"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="No">No</SelectItem>
                    <SelectItem value="Yes">Yes</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {formData.event_gift === 'Yes' && (
                <div className="space-y-2">
                  <Label>Per Person Gift Cost</Label>
                  <Input type="number" value={formData.event_gift_cost || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, event_gift_cost: e.target.value }))}
                    placeholder="Cost per person" data-testid="event-gift-cost" />
                </div>
              )}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Food Arrangement</Label>
                <Select value={formData.event_food_arrangement || 'No'}
                  onValueChange={(v) => setFormData(prev => ({ ...prev, event_food_arrangement: v }))}>
                  <SelectTrigger data-testid="event-food-arrangement"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="No">No</SelectItem>
                    <SelectItem value="Yes">Yes</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {formData.event_food_arrangement === 'Yes' && (
                <div className="space-y-2">
                  <Label>Per Person Food Budget</Label>
                  <Input type="number" value={formData.event_food_budget || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, event_food_budget: e.target.value }))}
                    placeholder="Budget per person" data-testid="event-food-budget" />
                </div>
              )}
            </div>
            <div className="space-y-2">
              <Label>Special Requirements</Label>
              <Textarea value={formData.event_requirements}
                onChange={(e) => setFormData(prev => ({ ...prev, event_requirements: e.target.value }))}
                placeholder="AV equipment, decorations, etc." rows={2} data-testid="event-requirements" />
            </div>
          </>
        );

      case 'Onboarding':
        return (
          <>
            <div className="mb-2">
              <h3 className="text-lg font-semibold">Onboarding</h3>
              <p className="text-sm text-muted-foreground">Onboarding Request</p>
            </div>
            {/* Basic Details Section */}
            <div className="space-y-4 border-b pb-4">
              <h3 className="font-semibold text-lg">Basic Details</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Employee Name *</Label>
                  <Input
                    value={formData.induction_employee_name}
                    onChange={(e) => setFormData(prev => ({ ...prev, induction_employee_name: e.target.value }))}
                    placeholder="Full name"
                    data-testid="induction-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Employee ID *</Label>
                  <Input
                    value={formData.induction_employee_id}
                    onChange={(e) => setFormData(prev => ({ ...prev, induction_employee_id: e.target.value }))}
                    placeholder="Employee code"
                    data-testid="induction-id"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Designation *</Label>
                  <Input
                    value={formData.induction_designation}
                    onChange={(e) => setFormData(prev => ({ ...prev, induction_designation: e.target.value }))}
                    placeholder="Job title/Designation"
                    data-testid="induction-designation"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Joining Date *</Label>
                  <Input
                    type="date"
                    value={formData.induction_joining_date}
                    onChange={(e) => setFormData(prev => ({ ...prev, induction_joining_date: e.target.value }))}
                    data-testid="induction-date"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Department *</Label>
                  <Select
                    value={formData.induction_department}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, induction_department: value }))}
                  >
                    <SelectTrigger data-testid="induction-department">
                      <SelectValue placeholder="Select department" />
                    </SelectTrigger>
                    <SelectContent>
                      {employeeDepartments.map((dept) => (
                        <SelectItem key={dept} value={dept}>
                          {dept}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Employee Type *</Label>
                  <Select
                    value={formData.induction_employee_type}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, induction_employee_type: value }))}
                  >
                    <SelectTrigger data-testid="induction-employee-type">
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Permanent">Permanent</SelectItem>
                      <SelectItem value="Contract">Contract</SelectItem>
                      <SelectItem value="Intern">Intern</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Entity *</Label>
                  <Select
                    value={formData.induction_entity}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, induction_entity: value }))}
                  >
                    <SelectTrigger data-testid="induction-entity">
                      <SelectValue placeholder="Select entity" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="UOSPL">UOSPL</SelectItem>
                      <SelectItem value="UAFPL">UAFPL</SelectItem>
                      <SelectItem value="UAPL">UAPL</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>T-Shirt Size</Label>
                  <Select
                    value={formData.induction_tshirt_size}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, induction_tshirt_size: value }))}
                  >
                    <SelectTrigger data-testid="induction-tshirt">
                      <SelectValue placeholder="Select size" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="XS">XS</SelectItem>
                      <SelectItem value="S">S</SelectItem>
                      <SelectItem value="M">M</SelectItem>
                      <SelectItem value="L">L</SelectItem>
                      <SelectItem value="XL">XL</SelectItem>
                      <SelectItem value="XXL">XXL</SelectItem>
                      <SelectItem value="XXXL">XXXL</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* ID Card Section (Conditional) */}
            <div className="space-y-4 border-b pb-4">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-lg">ID Card Required?</h3>
                <Select
                  value={formData.induction_need_id_card}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, induction_need_id_card: value }))}
                >
                  <SelectTrigger className="w-24" data-testid="induction-need-id-card">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="yes">Yes</SelectItem>
                    <SelectItem value="no">No</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {formData.induction_need_id_card === 'yes' && (
                <div className="space-y-4 bg-muted/30 p-4 rounded-lg">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Date of Birth *</Label>
                      <Input
                        type="date"
                        value={formData.induction_dob}
                        onChange={(e) => setFormData(prev => ({ ...prev, induction_dob: e.target.value }))}
                        data-testid="induction-dob"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Date of Issue</Label>
                      <Input
                        type="date"
                        value={formData.induction_date_of_issue}
                        onChange={(e) => setFormData(prev => ({ ...prev, induction_date_of_issue: e.target.value }))}
                        data-testid="induction-issue-date"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Blood Group</Label>
                      <Select
                        value={formData.induction_blood_group}
                        onValueChange={(value) => setFormData(prev => ({ ...prev, induction_blood_group: value }))}
                      >
                        <SelectTrigger data-testid="induction-blood-group">
                          <SelectValue placeholder="Select blood group" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="A+">A+</SelectItem>
                          <SelectItem value="A-">A-</SelectItem>
                          <SelectItem value="B+">B+</SelectItem>
                          <SelectItem value="B-">B-</SelectItem>
                          <SelectItem value="AB+">AB+</SelectItem>
                          <SelectItem value="AB-">AB-</SelectItem>
                          <SelectItem value="O+">O+</SelectItem>
                          <SelectItem value="O-">O-</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Emergency Contact Number *</Label>
                      <Input
                        type="tel"
                        value={formData.induction_emergency_number}
                        onChange={(e) => setFormData(prev => ({ ...prev, induction_emergency_number: e.target.value }))}
                        placeholder="Emergency contact"
                        data-testid="induction-emergency"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Masked Aadhaar (Last 4 digits)</Label>
                      <div className="flex items-center gap-2">
                        <span className="text-muted-foreground text-sm">XXXX-XXXX-</span>
                        <Input
                          value={formData.induction_masked_aadhaar}
                          onChange={(e) => {
                            const value = e.target.value.replace(/\D/g, '').slice(0, 4);
                            setFormData(prev => ({ ...prev, induction_masked_aadhaar: value }));
                          }}
                          placeholder="1234"
                          maxLength={4}
                          className="w-24"
                          data-testid="induction-aadhaar"
                        />
                      </div>
                      <p className="text-xs text-muted-foreground">Only last 4 digits for security</p>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Office Address</Label>
                    <Textarea
                      value={formData.induction_office_address}
                      onChange={(e) => setFormData(prev => ({ ...prev, induction_office_address: e.target.value }))}
                      placeholder="Office address for ID card"
                      rows={2}
                      data-testid="induction-office-address"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Mediclaim Section (Conditional) */}
            <div className="space-y-4 border-b pb-4">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-lg">Mediclaim Required?</h3>
                <Select
                  value={formData.induction_need_mediclaim}
                  onValueChange={(value) => {
                    setFormData(prev => ({ 
                      ...prev, 
                      induction_need_mediclaim: value,
                      induction_mediclaim_members: value === 'yes' && prev.induction_mediclaim_members.length === 0 
                        ? [{ ...emptyMediclaimMember }] 
                        : prev.induction_mediclaim_members
                    }));
                  }}
                >
                  <SelectTrigger className="w-24" data-testid="induction-need-mediclaim">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="yes">Yes</SelectItem>
                    <SelectItem value="no">No</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {formData.induction_need_mediclaim === 'yes' && (
                <div className="space-y-4 bg-muted/30 p-4 rounded-lg">
                  {formData.induction_mediclaim_members.map((member, index) => (
                    <div key={index} className="space-y-3 border-b pb-4 last:border-b-0">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium flex items-center gap-2">
                          <User className="w-4 h-4" />
                          Family Member {index + 1}
                        </span>
                        {formData.induction_mediclaim_members.length > 1 && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => removeMediclaimMember(index)}
                            className="text-red-500 hover:text-red-700"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <Label className="text-xs">Full Name *</Label>
                          <Input
                            value={member.full_name}
                            onChange={(e) => updateMediclaimMember(index, 'full_name', e.target.value)}
                            placeholder="Full name"
                            className="h-9"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Relation *</Label>
                          <Select
                            value={member.relation}
                            onValueChange={(value) => updateMediclaimMember(index, 'relation', value)}
                          >
                            <SelectTrigger className="h-9">
                              <SelectValue placeholder="Select relation" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="Self">Self</SelectItem>
                              <SelectItem value="Spouse">Spouse</SelectItem>
                              <SelectItem value="Son">Son</SelectItem>
                              <SelectItem value="Daughter">Daughter</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <Label className="text-xs">Date of Birth *</Label>
                          <Input
                            type="date"
                            value={member.dob}
                            onChange={(e) => updateMediclaimMember(index, 'dob', e.target.value)}
                            className="h-9"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Date of Joining</Label>
                          <Input
                            type="date"
                            value={member.doj}
                            onChange={(e) => updateMediclaimMember(index, 'doj', e.target.value)}
                            className="h-9"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <Label className="text-xs">Grade</Label>
                          <Input
                            value={member.grade}
                            onChange={(e) => updateMediclaimMember(index, 'grade', e.target.value)}
                            placeholder="Grade/Level"
                            className="h-9"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Sum Insured</Label>
                          <Input
                            type="number"
                            value={member.sum_insured}
                            onChange={(e) => updateMediclaimMember(index, 'sum_insured', e.target.value)}
                            placeholder="Amount"
                            className="h-9"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                  <Button
                    type="button"
                    variant="outline"
                    onClick={addMediclaimMember}
                    className="w-full"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Family Member
                  </Button>
                </div>
              )}
            </div>

            {/* Facility Requirements (Multiple Select) */}
            <div className="space-y-4">
              <h3 className="font-semibold text-lg">Facility Requirements</h3>
              <div className="flex flex-wrap gap-6">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="visiting-card"
                    checked={formData.induction_facility_visiting_card}
                    onCheckedChange={(checked) => setFormData(prev => ({ ...prev, induction_facility_visiting_card: checked }))}
                  />
                  <Label htmlFor="visiting-card" className="cursor-pointer">Visiting Card</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="hotel"
                    checked={formData.induction_facility_hotel}
                    onCheckedChange={(checked) => setFormData(prev => ({ ...prev, induction_facility_hotel: checked }))}
                  />
                  <Label htmlFor="hotel" className="cursor-pointer">Hotel</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="transport"
                    checked={formData.induction_facility_transport}
                    onCheckedChange={(checked) => setFormData(prev => ({ ...prev, induction_facility_transport: checked }))}
                  />
                  <Label htmlFor="transport" className="cursor-pointer">Transport</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Label className="mr-2">Food Required:</Label>
                  <Select
                    value={formData.induction_food_required}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, induction_food_required: value }))}
                  >
                    <SelectTrigger className="w-20" data-testid="induction-food-required">
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="yes">Yes</SelectItem>
                      <SelectItem value="no">No</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          </>
        );

      case 'Insurance Removal Request':
        return (
          <>
            {formData.insurance_employees.length === 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Employee Name *</Label>
                  <Input
                    value={formData.insurance_employee_name}
                    onChange={(e) => setFormData(prev => ({ ...prev, insurance_employee_name: e.target.value }))}
                    placeholder="Full name of exiting employee"
                    data-testid="insurance-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Employee ID *</Label>
                  <Input
                    value={formData.insurance_employee_id}
                    onChange={(e) => setFormData(prev => ({ ...prev, insurance_employee_id: e.target.value }))}
                    placeholder="Employee code"
                    data-testid="insurance-id"
                  />
                </div>
              </div>
            )}
            {formData.insurance_employees.length === 0 && (
              <div className="space-y-2">
                <Label>Date of Exit *</Label>
                <Input
                  type="date"
                  value={formData.insurance_exit_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, insurance_exit_date: e.target.value }))}
                  data-testid="insurance-exit-date"
                />
              </div>
            )}
            
            {/* Multi-employee section */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-medium">Employees for Insurance Removal</Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    if (formData.insurance_employees.length === 0 && formData.insurance_employee_name) {
                      setFormData(prev => ({
                        ...prev,
                        insurance_employees: [
                          { name: prev.insurance_employee_name, id: prev.insurance_employee_id, exit_date: prev.insurance_exit_date, department: '', relation: '', dob: '' },
                          { name: '', id: '', exit_date: '', department: '', relation: '', dob: '' }
                        ],
                        insurance_employee_name: '',
                        insurance_employee_id: '',
                        insurance_exit_date: ''
                      }));
                    } else {
                      setFormData(prev => ({
                        ...prev,
                        insurance_employees: [...prev.insurance_employees, { name: '', id: '', exit_date: '', department: '', relation: '', dob: '' }]
                      }));
                    }
                  }}
                  data-testid="add-insurance-employee-btn"
                >
                  <Plus className="w-4 h-4 mr-1" /> Add Employee
                </Button>
              </div>
              
              {formData.insurance_employees.map((emp, index) => (
                <div key={index} className="p-3 border rounded-lg space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">Employee {index + 1}</span>
                    <Button type="button" variant="ghost" size="sm"
                      onClick={() => setFormData(prev => ({ ...prev, insurance_employees: prev.insurance_employees.filter((_, i) => i !== index) }))}
                    >
                      <Trash2 className="w-4 h-4 text-destructive" />
                    </Button>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs">Employee Name *</Label>
                      <Input value={emp.name} onChange={(e) => {
                        const u = [...formData.insurance_employees]; u[index] = { ...u[index], name: e.target.value };
                        setFormData(prev => ({ ...prev, insurance_employees: u }));
                      }} placeholder="Full name" />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Employee Code *</Label>
                      <Input value={emp.id} onChange={(e) => {
                        const u = [...formData.insurance_employees]; u[index] = { ...u[index], id: e.target.value };
                        setFormData(prev => ({ ...prev, insurance_employees: u }));
                      }} placeholder="Emp code" />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Department</Label>
                      <Input value={emp.department || ''} onChange={(e) => {
                        const u = [...formData.insurance_employees]; u[index] = { ...u[index], department: e.target.value };
                        setFormData(prev => ({ ...prev, insurance_employees: u }));
                      }} placeholder="Department" />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs">Relation</Label>
                      <Input value={emp.relation || ''} onChange={(e) => {
                        const u = [...formData.insurance_employees]; u[index] = { ...u[index], relation: e.target.value };
                        setFormData(prev => ({ ...prev, insurance_employees: u }));
                      }} placeholder="Relation" />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">DOB</Label>
                      <Input type="date" value={emp.dob || ''} onChange={(e) => {
                        const u = [...formData.insurance_employees]; u[index] = { ...u[index], dob: e.target.value };
                        setFormData(prev => ({ ...prev, insurance_employees: u }));
                      }} />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Date of Exit *</Label>
                      <Input type="date" value={emp.exit_date} onChange={(e) => {
                        const u = [...formData.insurance_employees]; u[index] = { ...u[index], exit_date: e.target.value };
                        setFormData(prev => ({ ...prev, insurance_employees: u }));
                      }} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        );

      case 'Meeting Room Booking':
        const roomLocations = Object.keys(roomConfig);
        const currentRooms = getRoomsForSelection(formData.room_location, formData.room_attendees);
        const triggerAvailabilityCheck = (loc, date, start, end) => {
          if (loc && date && start && end) {
            checkRoomAvailability(loc, date, start, end);
          }
        };
        return (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Location *</Label>
                <Select
                  value={formData.room_location}
                  onValueChange={(value) => {
                    setFormData(prev => ({ ...prev, room_location: value, room_type: '' }));
                    setRoomConflict(null);
                    setRoomSchedule([]);
                    setUnavailableRooms([]);
                    triggerAvailabilityCheck(value, formData.room_date, formData.room_start_time, formData.room_end_time);
                  }}
                >
                  <SelectTrigger data-testid="room-location">
                    <SelectValue placeholder="Select location" />
                  </SelectTrigger>
                  <SelectContent>
                    {roomLocations.map((loc) => (
                      <SelectItem key={loc} value={loc}>{loc}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Number of Attendees *</Label>
                <Input
                  type="number"
                  min="1"
                  value={formData.room_attendees}
                  onChange={(e) => {
                    const val = e.target.value;
                    setFormData(prev => ({ ...prev, room_attendees: val, room_type: '' }));
                    setRoomConflict(null);
                    setRoomSchedule([]);
                    triggerAvailabilityCheck(formData.room_location, formData.room_date, formData.room_start_time, formData.room_end_time);
                  }}
                  placeholder="Number of attendees"
                  data-testid="room-attendees"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Meeting Category *</Label>
              <Select
                value={formData.room_meeting_category}
                onValueChange={(value) => setFormData(prev => ({ ...prev, room_meeting_category: value }))}
              >
                <SelectTrigger data-testid="room-meeting-category">
                  <SelectValue placeholder="Select meeting category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Internal">Internal</SelectItem>
                  <SelectItem value="Client">Client</SelectItem>
                  <SelectItem value="Dealer">Dealer</SelectItem>
                  <SelectItem value="Supplier">Supplier</SelectItem>
                  <SelectItem value="Partner">Partner</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Date *</Label>
              <Input
                type="date"
                value={formData.room_date}
                onChange={(e) => {
                  const newDate = e.target.value;
                  setFormData(prev => ({ ...prev, room_date: newDate }));
                  if (formData.room_location && formData.room_type) {
                    fetchRoomSchedule(formData.room_location, formData.room_type, newDate);
                  }
                  if (formData.room_location && formData.room_type && formData.room_start_time && formData.room_end_time) {
                    checkRoomConflict(formData.room_location, formData.room_type, newDate, formData.room_start_time, formData.room_end_time);
                  }
                  triggerAvailabilityCheck(formData.room_location, newDate, formData.room_start_time, formData.room_end_time);
                }}
                data-testid="room-date"
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Start Time *</Label>
                <TimePicker12h
                  value={formData.room_start_time}
                  onChange={(newStart) => {
                    setFormData(prev => ({ ...prev, room_start_time: newStart }));
                    if (formData.room_location && formData.room_type && formData.room_date && formData.room_end_time) {
                      checkRoomConflict(formData.room_location, formData.room_type, formData.room_date, newStart, formData.room_end_time);
                    }
                    triggerAvailabilityCheck(formData.room_location, formData.room_date, newStart, formData.room_end_time);
                  }}
                  data-testid="room-start-time"
                />
              </div>
              <div className="space-y-2">
                <Label>End Time *</Label>
                <TimePicker12h
                  value={formData.room_end_time}
                  onChange={(newEnd) => {
                    setFormData(prev => ({ ...prev, room_end_time: newEnd }));
                    if (formData.room_location && formData.room_type && formData.room_date && formData.room_start_time) {
                      checkRoomConflict(formData.room_location, formData.room_type, formData.room_date, formData.room_start_time, newEnd);
                    }
                    triggerAvailabilityCheck(formData.room_location, formData.room_date, formData.room_start_time, newEnd);
                  }}
                  data-testid="room-end-time"
                />
              </div>
            </div>

            {/* Room Selection - based on location + attendees */}
            {formData.room_location && formData.room_attendees && (
              <div className="space-y-2">
                <Label>Room * {formData.room_location !== 'Vankas' && (
                  <span className="text-xs text-muted-foreground ml-1">
                    ({parseInt(formData.room_attendees) <= 4 ? 'Small rooms' : 'Large rooms'} for {formData.room_attendees} attendees)
                  </span>
                )}</Label>
                <Select
                  value={formData.room_type}
                  onValueChange={(value) => {
                    const isUnavailable = unavailableRooms.includes(value);
                    if (isUnavailable) {
                      toast.error('This room is already booked for the selected time.');
                      return;
                    }
                    setFormData(prev => ({ ...prev, room_type: value }));
                    if (formData.room_date) {
                      fetchRoomSchedule(formData.room_location, value, formData.room_date);
                    }
                    if (formData.room_date && formData.room_start_time && formData.room_end_time) {
                      checkRoomConflict(formData.room_location, value, formData.room_date, formData.room_start_time, formData.room_end_time);
                    }
                  }}
                >
                  <SelectTrigger data-testid="room-type">
                    <SelectValue placeholder="Select room" />
                  </SelectTrigger>
                  <SelectContent>
                    {currentRooms.map((room) => {
                      const isUnavailable = unavailableRooms.includes(room);
                      return (
                        <SelectItem key={room} value={room} disabled={isUnavailable} className={isUnavailable ? 'opacity-50' : ''}>
                          {room}{isUnavailable ? ' - Unavailable' : ''}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Conflict Warning */}
            {roomConflict && roomConflict.has_conflict && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg" data-testid="room-conflict-warning">
                <p className="text-sm font-medium text-red-800">This room is already booked for the selected time.</p>
                {roomConflict.conflicts.map((c, i) => (
                  <div key={i} className="text-xs text-red-600 mt-1">
                    {c.start_time} - {c.end_time} by {c.booked_by} ({c.ticket_id})
                  </div>
                ))}
              </div>
            )}

            {/* Room Schedule Viewer */}
            {roomSchedule.length > 0 && (
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg" data-testid="room-schedule">
                <p className="text-sm font-medium text-blue-800 mb-2">
                  Schedule for {formData.room_type} on {formData.room_date}
                </p>
                <div className="space-y-1">
                  {roomSchedule.map((booking, i) => (
                    <div key={i} className="flex items-center justify-between text-xs bg-white px-2 py-1 rounded border">
                      <span className="font-medium">{booking.room_start_time} - {booking.room_end_time}</span>
                      <span className="text-muted-foreground">{booking.requester_name}</span>
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                        booking.status === 'Approved' ? 'bg-green-100 text-green-700' : 
                        booking.status === 'Open' ? 'bg-yellow-100 text-yellow-700' : 'bg-gray-100 text-gray-700'
                      }`}>
                        {booking.status}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {formData.room_location && formData.room_type && formData.room_date && roomSchedule.length === 0 && (
              <div className="p-2 bg-green-50 border border-green-200 rounded-lg text-xs text-green-700" data-testid="room-available">
                No bookings for this room on the selected date. Room is available!
              </div>
            )}

            <div className="space-y-2">
              <Label>Special Requirements</Label>
              <Textarea
                value={formData.room_requirements}
                onChange={(e) => setFormData(prev => ({ ...prev, room_requirements: e.target.value }))}
                placeholder="Projector, whiteboard, video conferencing, refreshments, etc."
                rows={2}
                data-testid="room-requirements"
              />
            </div>
          </>
        );

      case 'Promotion Box Request':
        return (
          <>
            <div className="space-y-2">
              <Label>Entity *</Label>
              <Select value={formData.promo_entity} onValueChange={(v) => setFormData(prev => ({ ...prev, promo_entity: v }))}>
                <SelectTrigger data-testid="promo-entity"><SelectValue placeholder="Select entity" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="UOSPL">UOSPL</SelectItem>
                  <SelectItem value="UAFPL">UAFPL</SelectItem>
                  <SelectItem value="UAPL">UAPL</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-medium">Employee Entries</Label>
                <div className="flex gap-2">
                  <Button type="button" variant="outline" size="sm"
                    onClick={async () => {
                      try {
                        const res = await fetch(`${API}/templates/promotion-box`, { headers: getAuthHeader(),  });
                        if (res.ok) {
                          const blob = await res.blob();
                          const url = URL.createObjectURL(blob);
                          const a = document.createElement('a'); a.href = url; a.download = 'promotion_box_template.xlsx'; a.click();
                          URL.revokeObjectURL(url);
                        }
                      } catch (err) { toast.error('Failed to download template'); }
                    }}
                    data-testid="promo-download-template-btn"
                  >
                    <Download className="w-4 h-4 mr-1" /> Template
                  </Button>
                  <Button type="button" variant="outline" size="sm"
                    onClick={() => document.getElementById('promo-excel-upload').click()}
                    data-testid="promo-import-excel-btn"
                  >
                    <FileSpreadsheet className="w-4 h-4 mr-1" /> Import Excel
                  </Button>
                  <input id="promo-excel-upload" type="file" accept=".xlsx,.xls" className="hidden"
                    onChange={async (e) => {
                      const file = e.target.files[0];
                      if (!file) return;
                      const fd = new FormData(); fd.append('file', file);
                      try {
                        // Upload file as attachment
                        const res = await fetch(`${API}/upload`, {
                          method: 'POST', headers: getAuthHeader(),  body: fd
                        });
                        if (res.ok) {
                          const data = await res.json();
                          setFormData(prev => ({ ...prev, attachments: [...prev.attachments, data.url] }));
                          toast.success(`Excel file "${file.name}" attached successfully`);
                        } else { toast.error('Failed to upload Excel file'); }
                      } catch (err) { toast.error('Failed to upload Excel'); }
                      e.target.value = '';
                    }}
                  />
                  <Button type="button" variant="outline" size="sm"
                    onClick={() => setFormData(prev => ({
                      ...prev,
                      promo_employees: [...prev.promo_employees, { employee_code: '', employee_name: '', employee_address: '', receiver_name: '', receiver_address: '', receiver_contact: '' }]
                    }))}
                    data-testid="add-promo-employee-btn"
                  >
                    <Plus className="w-4 h-4 mr-1" /> Add Employee
                  </Button>
                </div>
              </div>
              {formData.promo_employees.length === 0 && (
                <div className="text-center py-4 text-sm text-muted-foreground border rounded-lg border-dashed">
                  Click "Add Another Employee" to add employee entries
                </div>
              )}
              {formData.promo_employees.map((emp, index) => (
                <div key={index} className="p-3 border rounded-lg space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">Employee {index + 1}</span>
                    <Button type="button" variant="ghost" size="sm"
                      onClick={() => setFormData(prev => ({ ...prev, promo_employees: prev.promo_employees.filter((_, i) => i !== index) }))}
                    ><Trash2 className="w-4 h-4 text-destructive" /></Button>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                    <div className="space-y-1"><Label className="text-xs">Employee Code *</Label>
                      <Input value={emp.employee_code} onChange={(e) => { const u = [...formData.promo_employees]; u[index] = { ...u[index], employee_code: e.target.value }; setFormData(prev => ({ ...prev, promo_employees: u })); }} placeholder="Emp code" />
                    </div>
                    <div className="space-y-1"><Label className="text-xs">Employee Name *</Label>
                      <Input value={emp.employee_name} onChange={(e) => { const u = [...formData.promo_employees]; u[index] = { ...u[index], employee_name: e.target.value }; setFormData(prev => ({ ...prev, promo_employees: u })); }} placeholder="Full name" />
                    </div>
                    <div className="space-y-1"><Label className="text-xs">Employee Address *</Label>
                      <Input value={emp.employee_address} onChange={(e) => { const u = [...formData.promo_employees]; u[index] = { ...u[index], employee_address: e.target.value }; setFormData(prev => ({ ...prev, promo_employees: u })); }} placeholder="Address" />
                    </div>
                  </div>
                  <div className="text-xs font-medium text-muted-foreground mt-1">Receiver Details</div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                    <div className="space-y-1"><Label className="text-xs">Receiver Name *</Label>
                      <Input value={emp.receiver_name} onChange={(e) => { const u = [...formData.promo_employees]; u[index] = { ...u[index], receiver_name: e.target.value }; setFormData(prev => ({ ...prev, promo_employees: u })); }} placeholder="Receiver name" />
                    </div>
                    <div className="space-y-1"><Label className="text-xs">Receiver Address *</Label>
                      <Input value={emp.receiver_address} onChange={(e) => { const u = [...formData.promo_employees]; u[index] = { ...u[index], receiver_address: e.target.value }; setFormData(prev => ({ ...prev, promo_employees: u })); }} placeholder="Receiver address" />
                    </div>
                    <div className="space-y-1"><Label className="text-xs">Contact Number *</Label>
                      <Input value={emp.receiver_contact} onChange={(e) => { const u = [...formData.promo_employees]; u[index] = { ...u[index], receiver_contact: e.target.value }; setFormData(prev => ({ ...prev, promo_employees: u })); }} placeholder="Contact" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        );

      case 'Desk Decor – Birthday':
        return (
          <>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-medium">Birthday Celebration Entries</Label>
                <div className="flex gap-2">
                  <Button type="button" variant="outline" size="sm"
                    onClick={async () => {
                      try {
                        const res = await fetch(`${API}/templates/desk-decor`, { headers: getAuthHeader(),  });
                        if (res.ok) {
                          const blob = await res.blob();
                          const url = URL.createObjectURL(blob);
                          const a = document.createElement('a'); a.href = url; a.download = 'desk_decor_template.xlsx'; a.click();
                          URL.revokeObjectURL(url);
                        }
                      } catch (err) { toast.error('Failed to download template'); }
                    }}
                    data-testid="birthday-download-template-btn"
                  >
                    <Download className="w-4 h-4 mr-1" /> Template
                  </Button>
                  <Button type="button" variant="outline" size="sm"
                    onClick={() => document.getElementById('birthday-excel-upload').click()}
                    data-testid="birthday-import-excel-btn"
                  >
                    <FileSpreadsheet className="w-4 h-4 mr-1" /> Import Excel
                  </Button>
                  <input id="birthday-excel-upload" type="file" accept=".xlsx,.xls" className="hidden"
                    onChange={async (e) => {
                      const file = e.target.files[0];
                      if (!file) return;
                      const fd = new FormData(); fd.append('file', file);
                      try {
                        // Upload file as attachment
                        const res = await fetch(`${API}/upload`, {
                          method: 'POST', headers: getAuthHeader(),  body: fd
                        });
                        if (res.ok) {
                          const data = await res.json();
                          setFormData(prev => ({ ...prev, attachments: [...prev.attachments, data.url] }));
                          toast.success(`Excel file "${file.name}" attached successfully`);
                        } else { toast.error('Failed to upload Excel file'); }
                      } catch (err) { toast.error('Failed to upload Excel'); }
                      e.target.value = '';
                    }}
                  />
                  <Button type="button" variant="outline" size="sm"
                    onClick={() => setFormData(prev => ({
                      ...prev,
                      birthday_employees: [...prev.birthday_employees, { employee_name: '', required_by_date: '', department: '' }]
                    }))}
                    data-testid="add-birthday-employee-btn"
                  >
                    <Plus className="w-4 h-4 mr-1" /> Add Employee
                  </Button>
                </div>
              </div>
              
              {/* Single entry fallback if no multi-employee */}
              {formData.birthday_employees.length === 0 && (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Employee Name *</Label>
                      <Input value={formData.birthday_employee_name}
                        onChange={(e) => setFormData(prev => ({ ...prev, birthday_employee_name: e.target.value }))}
                        placeholder="Full name" data-testid="birthday-employee-name" />
                    </div>
                    <div className="space-y-2">
                      <Label>Required By Time *</Label>
                      <TimePicker12h value={formData.birthday_time}
                        onChange={(val) => setFormData(prev => ({ ...prev, birthday_time: val }))}
                        data-testid="birthday-time-input" />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Department *</Label>
                      <Select value={formData.birthday_department}
                        onValueChange={(value) => setFormData(prev => ({ ...prev, birthday_department: value }))}>
                        <SelectTrigger data-testid="birthday-department"><SelectValue placeholder="Select department" /></SelectTrigger>
                        <SelectContent>
                          {employeeDepartments.map((dept) => (
                            <SelectItem key={dept} value={dept}>{dept}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Date</Label>
                      <Input type="date" value={formData.birthday_date}
                        onChange={(e) => setFormData(prev => ({ ...prev, birthday_date: e.target.value }))}
                        data-testid="birthday-date" />
                    </div>
                  </div>
                </>
              )}
              
              {formData.birthday_employees.map((emp, index) => (
                <div key={index} className="p-3 border rounded-lg space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">Employee {index + 1}</span>
                    <Button type="button" variant="ghost" size="sm"
                      onClick={() => setFormData(prev => ({ ...prev, birthday_employees: prev.birthday_employees.filter((_, i) => i !== index) }))}
                    ><Trash2 className="w-4 h-4 text-destructive" /></Button>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                    <div className="space-y-1"><Label className="text-xs">Employee Name *</Label>
                      <Input value={emp.employee_name} onChange={(e) => { const u = [...formData.birthday_employees]; u[index] = { ...u[index], employee_name: e.target.value }; setFormData(prev => ({ ...prev, birthday_employees: u })); }} placeholder="Full name" />
                    </div>
                    <div className="space-y-1"><Label className="text-xs">Required By Time *</Label>
                      <Input type="time" value={emp.required_by_date} onChange={(e) => { const u = [...formData.birthday_employees]; u[index] = { ...u[index], required_by_date: e.target.value }; setFormData(prev => ({ ...prev, birthday_employees: u })); }} />
                    </div>
                    <div className="space-y-1"><Label className="text-xs">Department *</Label>
                      <Select value={emp.department} onValueChange={(v) => { const u = [...formData.birthday_employees]; u[index] = { ...u[index], department: v }; setFormData(prev => ({ ...prev, birthday_employees: u })); }}>
                        <SelectTrigger><SelectValue placeholder="Dept" /></SelectTrigger>
                        <SelectContent>
                          {employeeDepartments.map((dept) => (<SelectItem key={dept} value={dept}>{dept}</SelectItem>))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        );

      case 'Insurance Addition Request':
        return (
          <>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-medium">Employee Insurance Entries</Label>
                <Button type="button" variant="outline" size="sm"
                  onClick={() => setFormData(prev => ({
                    ...prev,
                    insurance_add_employees: [...prev.insurance_add_employees, { employee_code: '', employee_name: '', department: '', grade: '', sum_insured: '', insurance_required_for: '', member_name: '', member_dob: '' }]
                  }))}
                  data-testid="add-ins-add-employee-btn"
                >
                  <Plus className="w-4 h-4 mr-1" /> Add Another Employee
                </Button>
              </div>
              {formData.insurance_add_employees.length === 0 && (
                <div className="text-center py-4 text-sm text-muted-foreground border rounded-lg border-dashed">
                  Click "Add Another Employee" to add insurance addition entries
                </div>
              )}
              {formData.insurance_add_employees.map((emp, index) => (
                <div key={index} className="p-3 border rounded-lg space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">Employee {index + 1}</span>
                    <Button type="button" variant="ghost" size="sm"
                      onClick={() => setFormData(prev => ({ ...prev, insurance_add_employees: prev.insurance_add_employees.filter((_, i) => i !== index) }))}
                    ><Trash2 className="w-4 h-4 text-destructive" /></Button>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                    <div className="space-y-1"><Label className="text-xs">Employee Code *</Label>
                      <Input value={emp.employee_code} onChange={(e) => { const u = [...formData.insurance_add_employees]; u[index] = { ...u[index], employee_code: e.target.value }; setFormData(prev => ({ ...prev, insurance_add_employees: u })); }} placeholder="Emp code" />
                    </div>
                    <div className="space-y-1"><Label className="text-xs">Employee Name *</Label>
                      <Input value={emp.employee_name} onChange={(e) => { const u = [...formData.insurance_add_employees]; u[index] = { ...u[index], employee_name: e.target.value }; setFormData(prev => ({ ...prev, insurance_add_employees: u })); }} placeholder="Full name" />
                    </div>
                    <div className="space-y-1"><Label className="text-xs">Department</Label>
                      <Input value={emp.department} onChange={(e) => { const u = [...formData.insurance_add_employees]; u[index] = { ...u[index], department: e.target.value }; setFormData(prev => ({ ...prev, insurance_add_employees: u })); }} placeholder="Department" />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                    <div className="space-y-1"><Label className="text-xs">Grade</Label>
                      <Input value={emp.grade} onChange={(e) => { const u = [...formData.insurance_add_employees]; const newGrade = e.target.value; u[index] = { ...u[index], grade: newGrade }; if (newGrade && gradeSumMapping[newGrade]) { u[index].sum_insured = String(gradeSumMapping[newGrade]); } setFormData(prev => ({ ...prev, insurance_add_employees: u })); }} placeholder="Grade" />
                    </div>
                    <div className="space-y-1"><Label className="text-xs">Sum Insured</Label>
                      <Input type="number" value={emp.sum_insured} onChange={(e) => { const u = [...formData.insurance_add_employees]; u[index] = { ...u[index], sum_insured: e.target.value }; setFormData(prev => ({ ...prev, insurance_add_employees: u })); }} placeholder="Amount" />
                    </div>
                    <div className="space-y-1"><Label className="text-xs">Insurance Required For *</Label>
                      <Select value={emp.insurance_required_for} onValueChange={(v) => { const u = [...formData.insurance_add_employees]; u[index] = { ...u[index], insurance_required_for: v }; setFormData(prev => ({ ...prev, insurance_add_employees: u })); }}>
                        <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Self">Self</SelectItem>
                          <SelectItem value="Spouse">Spouse</SelectItem>
                          <SelectItem value="Son">Son</SelectItem>
                          <SelectItem value="Daughter">Daughter</SelectItem>
                          <SelectItem value="New Born Baby">New Born Baby</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="space-y-1"><Label className="text-xs">Member Name</Label>
                      <Input value={emp.member_name} onChange={(e) => { const u = [...formData.insurance_add_employees]; u[index] = { ...u[index], member_name: e.target.value }; setFormData(prev => ({ ...prev, insurance_add_employees: u })); }} placeholder="Name" />
                    </div>
                    <div className="space-y-1"><Label className="text-xs">Member DOB *</Label>
                      <Input type="date" value={emp.member_dob} onChange={(e) => { const u = [...formData.insurance_add_employees]; u[index] = { ...u[index], member_dob: e.target.value }; setFormData(prev => ({ ...prev, insurance_add_employees: u })); }} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        );

      case 'Repairs & Maintenance':
        return (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Maintenance Category *</Label>
                <Select value={formData.maintenance_category} onValueChange={(v) => setFormData(prev => ({ ...prev, maintenance_category: v }))}>
                  <SelectTrigger data-testid="maintenance-category"><SelectValue placeholder="Select category" /></SelectTrigger>
                  <SelectContent>
                    {MAINTENANCE_CATEGORIES.map(cat => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {formData.maintenance_category === 'Other' && (
                <div className="space-y-2">
                  <Label>Specify Category *</Label>
                  <Input value={formData.maintenance_other_text}
                    onChange={(e) => setFormData(prev => ({ ...prev, maintenance_other_text: e.target.value }))}
                    placeholder="Enter maintenance category" data-testid="maintenance-other-text" />
                </div>
              )}
            </div>
          </>
        );

      case 'Other':
        return (
          <div className="space-y-2">
            <Label>Specify Request Type *</Label>
            <Input
              value={formData.other_request_type}
              onChange={(e) => setFormData(prev => ({ ...prev, other_request_type: e.target.value }))}
              placeholder="Enter your request type"
              data-testid="other-request-type"
            />
          </div>
        );

      default:
        return null;
    }
  };

  // ---- Client Factory Visit: structured general fields + dynamic visit locations ----
  const CFV_LOCATIONS = ['Mumbai', 'Vankas (U1)', 'Malkhet (U2)'];
  const CFV_BRANDS = ['Univicoustic', 'Uflex', 'Unitile'];
  const CFV_ENTITIES = ['UOSPL', 'UAFPL', 'UAPL'];
  const CFV_DURATIONS = ['Full Day', 'Half Day', 'Overnight'];
  const emptySeg = () => ({ location: '', brand: '', entity: '', arrival_date: '', expected_time: '', departure_date: '', departure_time: '', duration: 'Full Day' });

  const startAddSeg = () => { setCfvEditIndex(null); setCfvSeg(emptySeg()); };
  const startEditSeg = (i) => { setCfvEditIndex(i); setCfvSeg({ ...formData.cfv_visit_locations[i] }); };
  const cancelSeg = () => { setCfvSeg(null); setCfvEditIndex(null); };
  const saveSeg = () => {
    if (!cfvSeg.location) { toast.error('Select a visit location'); return; }
    if (!cfvSeg.arrival_date) { toast.error('Select an arrival date'); return; }
    if (!cfvSeg.departure_date) { toast.error('Select a departure date'); return; }
    if (cfvSeg.departure_date < cfvSeg.arrival_date) { toast.error('Departure cannot be before arrival'); return; }
    setFormData(prev => {
      const list = [...(prev.cfv_visit_locations || [])];
      if (cfvEditIndex !== null) list[cfvEditIndex] = cfvSeg; else list.push(cfvSeg);
      return { ...prev, cfv_visit_locations: list };
    });
    cancelSeg();
  };
  const deleteSeg = (i) => setFormData(prev => ({ ...prev, cfv_visit_locations: prev.cfv_visit_locations.filter((_, idx) => idx !== i) }));

  const renderClientFactoryVisit = () => {
    const segs = formData.cfv_visit_locations || [];
    const CFV_MEALS = ['Breakfast', 'Lunch', 'Dinner'];
    const CFV_DIETARY = ['Veg', 'Non-Veg', 'Jain'];
    const CFV_GIVEAWAY = ['Univic Frames', 'Momentos', 'Royce', 'Bombay Sweet Shop / Equivalent Hampers', 'Others'];
    const CFV_SEGMENT = ['CRE', 'Data Centre', 'Control Rooms', 'Other'];
    const CFV_PURPOSE = ['Understand Production Capacity & Manufacturing Process', 'Product / Quality Inspection', 'Technical Discussion / Interaction with Technical Team', 'Product Demonstration & Product Range Understanding', 'Vendor / Supplier Evaluation', 'Other'];
    const CFV_PROJECT_STAGES = ['Concept', 'Design', 'Tendering', 'Execution', 'Completed'];
    const CFV_COUNTRIES = COUNTRIES;
    const foodOn = formData.cfv_food_required === 'Yes';
    const visitors = formData.cfv_visitors || [];
    const setVisitors = (v) => setFormData(prev => ({ ...prev, cfv_visitors: v, cfv_guests_count: String(v.length) }));
    const addVisitor = () => setVisitors([...visitors, { name: '', contact: '', email: '', designation: '', company: '', country: '', dietary: 'Veg' }]);
    const updVisitor = (i, k, val) => setVisitors(visitors.map((v, idx) => idx === i ? { ...v, [k]: val } : v));
    const rmVisitor = (i) => setVisitors(visitors.filter((_, idx) => idx !== i));
    const dietCounts = { Veg: 0, 'Non-Veg': 0, Jain: 0 };
    visitors.forEach(v => { if (v.dietary && dietCounts[v.dietary] !== undefined) dietCounts[v.dietary] += 1; });
    const toggleArr = (field, val) => setFormData(prev => { const a = prev[field] || []; return { ...prev, [field]: a.includes(val) ? a.filter(x => x !== val) : [...a, val] }; });
    const Chip = ({ field, val }) => {
      const on = (formData[field] || []).includes(val);
      return <button type="button" onClick={() => toggleArr(field, val)} data-testid={`${field}-${val}`}
        className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${on ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-background hover:bg-muted'}`}>{val}</button>;
    };
    const YesNo = ({ label, field, testid }) => (
      <div className="space-y-2">
        <Label className="block text-sm leading-snug min-h-[2.25rem]">{label}</Label>
        <Select value={formData[field]} onValueChange={(v) => setFormData(prev => ({ ...prev, [field]: v }))}>
          <SelectTrigger data-testid={testid}><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="Yes">Yes</SelectItem><SelectItem value="No">No</SelectItem></SelectContent>
        </Select>
      </div>
    );
    return (
      <div className="space-y-5" data-testid="cfv-form">
        {/* Visitors */}
        <div className="border rounded-xl p-4 space-y-3 bg-muted/10" data-testid="cfv-visitors">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <h4 className="font-semibold">Visitors *</h4>
              <p className="text-xs text-muted-foreground">Add every visitor with their company. No. of Guests: <span className="font-medium text-foreground" data-testid="cfv-guests-count">{visitors.length}</span></p>
            </div>
            <Button type="button" size="sm" variant="outline" onClick={addVisitor} data-testid="cfv-add-visitor"><Plus className="h-4 w-4 mr-1" />Add Visitor</Button>
          </div>
          {visitors.length === 0 && <p className="text-sm text-muted-foreground italic">No visitors added yet.</p>}
          {visitors.map((v, i) => (
            <div key={i} className="border rounded-lg p-3 bg-background space-y-2" data-testid={`cfv-visitor-row-${i}`}>
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-muted-foreground">Visitor {i + 1}</span>
                <Button type="button" size="icon" variant="ghost" className="h-8 w-8" onClick={() => rmVisitor(i)} data-testid={`cfv-visitor-remove-${i}`}><Trash2 className="h-4 w-4 text-red-600" /></Button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                <Input placeholder="Name *" value={v.name} onChange={e => updVisitor(i, 'name', e.target.value)} data-testid={`cfv-visitor-name-${i}`} />
                <Input placeholder="Contact *" value={v.contact} onChange={e => updVisitor(i, 'contact', e.target.value)} data-testid={`cfv-visitor-contact-${i}`} />
                <Input placeholder="Email *" value={v.email} onChange={e => updVisitor(i, 'email', e.target.value)} data-testid={`cfv-visitor-email-${i}`} />
                <Input placeholder="Company *" value={v.company || ''} onChange={e => updVisitor(i, 'company', e.target.value)} data-testid={`cfv-visitor-company-${i}`} />
                <Input placeholder="Designation" value={v.designation} onChange={e => updVisitor(i, 'designation', e.target.value)} />
                <Select value={v.country || ''} onValueChange={val => updVisitor(i, 'country', val)}>
                  <SelectTrigger data-testid={`cfv-visitor-country-${i}`}><SelectValue placeholder="Country" /></SelectTrigger>
                  <SelectContent>{CFV_COUNTRIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
          ))}
        </div>

        {/* Visit Locations */}
        <div className="border rounded-xl p-4 space-y-3 bg-muted/10" data-testid="cfv-visit-locations">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <h4 className="font-semibold">Visit Locations</h4>
              <p className="text-xs text-muted-foreground">Add each location with its own date and timing.</p>
            </div>
            {!cfvSeg && <Button type="button" size="sm" variant="outline" onClick={startAddSeg} data-testid="cfv-add-location"><Plus className="h-4 w-4 mr-1" />Add Visit Location</Button>}
          </div>

          {/* Itinerary summary table */}
          {segs.length > 0 && (
            <div className="overflow-x-auto rounded-lg border bg-background">
              <table className="w-full text-sm border-collapse" data-testid="cfv-itinerary">
                <thead>
                  <tr className="text-left text-xs text-muted-foreground border-b bg-muted/40">
                    <th className="py-2 px-3">Location</th><th className="py-2 px-3">Brand</th><th className="py-2 px-3">Entity</th>
                    <th className="py-2 px-3 whitespace-nowrap">Arrival</th><th className="py-2 px-3 whitespace-nowrap">Departure</th><th className="py-2 px-3">Duration</th><th className="py-2 px-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {segs.map((s, i) => (
                    <tr key={i} className="border-b last:border-0" data-testid={`cfv-seg-row-${i}`}>
                      <td className="py-2 px-3 font-medium">{s.location}</td>
                      <td className="py-2 px-3">{s.brand || '—'}</td>
                      <td className="py-2 px-3">{s.entity || '—'}</td>
                      <td className="py-2 px-3 whitespace-nowrap">{s.arrival_date} {s.expected_time || ''}</td>
                      <td className="py-2 px-3 whitespace-nowrap">{s.departure_date || '—'} {s.departure_time || ''}</td>
                      <td className="py-2 px-3">{s.duration}</td>
                      <td className="py-2 px-3 text-right whitespace-nowrap">
                        <Button type="button" size="icon" variant="ghost" className="h-8 w-8" onClick={() => startEditSeg(i)} data-testid={`cfv-edit-seg-${i}`}><Pencil className="h-4 w-4" /></Button>
                        <Button type="button" size="icon" variant="ghost" className="h-8 w-8" onClick={() => deleteSeg(i)} data-testid={`cfv-delete-seg-${i}`}><Trash2 className="h-4 w-4 text-red-600" /></Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Inline segment editor */}
          {cfvSeg && (
            <div className="border rounded-lg p-4 space-y-4 bg-background" data-testid="cfv-seg-editor">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="space-y-1">
                  <Label className="text-xs">Location *</Label>
                  <Select value={cfvSeg.location} onValueChange={(v) => setCfvSeg(s => ({ ...s, location: v }))}>
                    <SelectTrigger className="h-9 w-full" data-testid="cfv-seg-location"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>{CFV_LOCATIONS.map(l => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Brand to Visit</Label>
                  <Select value={cfvSeg.brand} onValueChange={(v) => setCfvSeg(s => ({ ...s, brand: v }))}>
                    <SelectTrigger className="h-9 w-full" data-testid="cfv-seg-brand"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>{CFV_BRANDS.map(b => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Entity</Label>
                  <Select value={cfvSeg.entity} onValueChange={(v) => setCfvSeg(s => ({ ...s, entity: v }))}>
                    <SelectTrigger className="h-9 w-full" data-testid="cfv-seg-entity"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>{CFV_ENTITIES.map(en => <SelectItem key={en} value={en}>{en}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Duration</Label>
                  <Select value={cfvSeg.duration} onValueChange={(v) => setCfvSeg(s => ({ ...s, duration: v }))}>
                    <SelectTrigger className="h-9 w-full" data-testid="cfv-seg-duration"><SelectValue /></SelectTrigger>
                    <SelectContent>{CFV_DURATIONS.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div className="space-y-1 border rounded-lg p-3 bg-muted/20">
                  <Label className="text-xs font-semibold">Arrival *</Label>
                  <div className="flex flex-wrap items-center gap-2">
                    <Input type="date" className="h-9 w-[150px]" value={cfvSeg.arrival_date} onChange={(e) => setCfvSeg(s => ({ ...s, arrival_date: e.target.value }))} data-testid="cfv-seg-date" />
                    <TimePicker12h value={cfvSeg.expected_time} onChange={(val) => setCfvSeg(s => ({ ...s, expected_time: val }))} data-testid="cfv-seg-time" />
                  </div>
                </div>
                <div className="space-y-1 border rounded-lg p-3 bg-muted/20">
                  <Label className="text-xs font-semibold">Departure *</Label>
                  <div className="flex flex-wrap items-center gap-2">
                    <Input type="date" className="h-9 w-[150px]" value={cfvSeg.departure_date} onChange={(e) => setCfvSeg(s => ({ ...s, departure_date: e.target.value }))} data-testid="cfv-seg-dep-date" />
                    <TimePicker12h value={cfvSeg.departure_time} onChange={(val) => setCfvSeg(s => ({ ...s, departure_time: val }))} data-testid="cfv-seg-dep-time" />
                  </div>
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" size="sm" variant="outline" onClick={cancelSeg}>Cancel</Button>
                <Button type="button" size="sm" onClick={saveSeg} data-testid="cfv-save-seg">{cfvEditIndex !== null ? 'Update Location' : 'Add to Itinerary'}</Button>
              </div>
            </div>
          )}

          {segs.length === 0 && !cfvSeg && <p className="text-sm text-muted-foreground italic">No visit locations added yet.</p>}
        </div>

        {/* Visit Details */}
        <div className="border rounded-xl p-4 space-y-4 bg-muted/10" data-testid="cfv-visit-details">
          <h4 className="font-semibold">Visit Details</h4>
          <div className="space-y-2">
            <Label>What is the primary purpose of your visit?</Label>
            <Select value={formData.cfv_primary_objective} onValueChange={(v) => setFormData(prev => ({ ...prev, cfv_primary_objective: v }))}>
              <SelectTrigger data-testid="cfv-primary-objective"><SelectValue placeholder="Select purpose" /></SelectTrigger>
              <SelectContent>{CFV_PURPOSE.map(p => <SelectItem key={p} value={p}>{p === 'Other' ? 'Other (please specify)' : p}</SelectItem>)}</SelectContent>
            </Select>
            {formData.cfv_primary_objective === 'Other' && (
              <Input className="mt-2" placeholder="Please specify *" value={formData.cfv_primary_objective_other} onChange={(e) => setFormData(prev => ({ ...prev, cfv_primary_objective_other: e.target.value }))} data-testid="cfv-primary-objective-other" />
            )}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <YesNo label="Is this visit linked to a specific project?" field="cfv_project_linked" testid="cfv-project-linked" />
            <div className="space-y-2">
              <Label>Industry / Segment</Label>
              <Select value={formData.cfv_industry_segment} onValueChange={(v) => setFormData(prev => ({ ...prev, cfv_industry_segment: v }))}>
                <SelectTrigger data-testid="cfv-industry-segment"><SelectValue placeholder="Select segment" /></SelectTrigger>
                <SelectContent>{CFV_SEGMENT.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
              {formData.cfv_industry_segment === 'Other' && (
                <Input className="mt-2" placeholder="Please specify *" value={formData.cfv_industry_segment_other} onChange={(e) => setFormData(prev => ({ ...prev, cfv_industry_segment_other: e.target.value }))} data-testid="cfv-industry-segment-other" />
              )}
            </div>
          </div>
          {formData.cfv_project_linked === 'Yes' && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 border rounded-lg p-3 bg-background" data-testid="cfv-project-details">
              <div className="space-y-2">
                <Label>Project Name</Label>
                <Input value={formData.cfv_project_name} onChange={(e) => setFormData(prev => ({ ...prev, cfv_project_name: e.target.value }))} data-testid="cfv-project-name" />
              </div>
              <div className="space-y-2">
                <Label>Location</Label>
                <Input value={formData.cfv_project_location} onChange={(e) => setFormData(prev => ({ ...prev, cfv_project_location: e.target.value }))} data-testid="cfv-project-location" />
              </div>
              <div className="space-y-2">
                <Label>Project Stage</Label>
                <Select value={formData.cfv_project_stage} onValueChange={(v) => setFormData(prev => ({ ...prev, cfv_project_stage: v }))}>
                  <SelectTrigger data-testid="cfv-project-stage"><SelectValue placeholder="Select stage" /></SelectTrigger>
                  <SelectContent>{CFV_PROJECT_STAGES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
          )}
        </div>

        {/* Visit Requirements */}
        <div className="border rounded-xl p-4 space-y-4 bg-muted/10" data-testid="cfv-requirements">
          <h4 className="font-semibold">Visit Requirements</h4>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
            <YesNo label="Need Invite" field="cfv_need_invite" testid="cfv-need-invite" />
            <YesNo label="Transport" field="cfv_transport" testid="cfv-transport" />
            <YesNo label="Photography" field="cfv_photography" testid="cfv-photography" />
            <YesNo label="Videography" field="cfv_videography" testid="cfv-videography" />
            <YesNo label="Car Parking Required" field="cfv_car_parking" testid="cfv-car-parking" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <YesNo label="Food Required" field="cfv_food_required" testid="cfv-food-required" />
          </div>
          {foodOn && (
            <div className="space-y-3 border rounded-lg p-3 bg-background">
              <div className="space-y-2">
                <Label>Meal Required</Label>
                <div className="flex flex-wrap gap-2" data-testid="cfv-meals">{CFV_MEALS.map(m => <Chip key={m} field="cfv_meals" val={m} />)}</div>
              </div>
              <div className="space-y-2">
                <Label>Dietary preference per visitor</Label>
                {visitors.length === 0 && <p className="text-xs text-muted-foreground italic">Add visitors above to set their dietary preference.</p>}
                <div className="space-y-2" data-testid="cfv-visitor-diet-list">
                  {visitors.map((v, i) => (
                    <div key={i} className="flex items-center justify-between gap-3 border rounded-md px-3 py-2 bg-muted/20">
                      <span className="text-sm font-medium truncate">{v.name?.trim() || `Visitor ${i + 1}`}</span>
                      <Select value={v.dietary || ''} onValueChange={val => updVisitor(i, 'dietary', val)}>
                        <SelectTrigger className="h-9 w-40 shrink-0" data-testid={`cfv-visitor-dietary-${i}`}><SelectValue placeholder="Select" /></SelectTrigger>
                        <SelectContent>{CFV_DIETARY.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  ))}
                </div>
                {visitors.length > 0 && (
                  <div className="text-sm bg-indigo-50 border border-indigo-200 rounded-lg p-3" data-testid="cfv-dietary-summary">
                    <span className="font-medium">Total:</span>{' '}
                    <span data-testid="cfv-diet-veg">Veg: {dietCounts.Veg}</span> ·{' '}
                    <span data-testid="cfv-diet-nonveg">Non-Veg: {dietCounts['Non-Veg']}</span> ·{' '}
                    <span data-testid="cfv-diet-jain">Jain: {dietCounts.Jain}</span>
                  </div>
                )}
              </div>
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <YesNo label="Stay Required" field="cfv_stay_required" testid="cfv-stay-required" />
            {formData.cfv_stay_required === 'Yes' && (
              <div className="space-y-2">
                <Label>Number of Nights</Label>
                <Input type="number" min="1" value={formData.cfv_stay_days} onChange={(e) => setFormData(prev => ({ ...prev, cfv_stay_days: e.target.value }))} data-testid="cfv-stay-days" />
              </div>
            )}
          </div>
          <div className="space-y-2">
            <Label>Giveaway / Momento</Label>
            <div className="flex flex-wrap gap-2 p-3 border rounded-lg bg-background" data-testid="cfv-giveaway">{CFV_GIVEAWAY.map(g => <Chip key={g} field="cfv_giveaway" val={g} />)}</div>
            {(formData.cfv_giveaway || []).includes('Others') && (
              <Input className="mt-2" placeholder="Please specify *" value={formData.cfv_giveaway_other} onChange={(e) => setFormData(prev => ({ ...prev, cfv_giveaway_other: e.target.value }))} data-testid="cfv-giveaway-other" />
            )}
          </div>
        </div>

        {/* Factory Visit Requirements */}
        <div className="border rounded-xl p-4 space-y-4 bg-muted/10" data-testid="cfv-factory-requirements">
          <h4 className="font-semibold">Factory Visit Requirements</h4>
          <div className="space-y-2">
            <Label>Products / Solutions to be demonstrated</Label>
            <Textarea value={formData.cfv_products_demonstrated} onChange={(e) => setFormData(prev => ({ ...prev, cfv_products_demonstrated: e.target.value }))} rows={2} data-testid="cfv-products-demonstrated" />
          </div>
          <YesNo label="Lab Testing / Demonstration Required" field="cfv_lab_testing" testid="cfv-lab-testing" />
        </div>

        {/* Internal Participation */}
        <div className="border rounded-xl p-4 space-y-4 bg-muted/10" data-testid="cfv-internal-participation">
          <h4 className="font-semibold">Internal Participation</h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <YesNo label="Senior Management Presence Required" field="cfv_senior_mgmt" testid="cfv-senior-mgmt" />
          </div>
        </div>

        {/* Presentation & Meeting Requirements */}
        <div className="border rounded-xl p-4 space-y-4 bg-muted/10" data-testid="cfv-presentation-requirements">
          <h4 className="font-semibold">Presentation &amp; Meeting Requirements</h4>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <YesNo label="Presentation Required" field="cfv_presentation" testid="cfv-presentation" />
            {formData.cfv_presentation === 'Yes' && (
              <div className="space-y-2">
                <Label>Type of Presentation</Label>
                <Input value={formData.cfv_presentation_type} onChange={(e) => setFormData(prev => ({ ...prev, cfv_presentation_type: e.target.value }))} placeholder="e.g. Standard / Customised / Project-specific" data-testid="cfv-presentation-type" />
              </div>
            )}
            <YesNo label="Conference Room Required" field="cfv_conference_room" testid="cfv-conference-room" />
          </div>
        </div>

        <p className="text-xs text-muted-foreground">Matching SPOCs (configured in SPOC Management) will be informed by email &amp; in-app for each visit location. They can view and comment but do not own the ticket.</p>
      </div>
    );
  };


  // Render custom fields for custom request types
  const renderCustomFields = () => {
    if (!selectedCustomType?.fields || selectedCustomType.fields.length === 0) {
      return (
        <p className="text-sm text-muted-foreground italic">
          No additional fields configured for this request type.
        </p>
      );
    }

    return (
      <div className="space-y-4">
        {selectedCustomType.fields.map((field) => (
          <div key={field.name} className="space-y-2">
            <Label>
              {field.label} {field.required && '*'}
            </Label>
            
            {field.type === 'text' && (
              <Input
                value={customFieldValues[field.name] || ''}
                onChange={(e) => setCustomFieldValues(prev => ({
                  ...prev,
                  [field.name]: e.target.value
                }))}
                placeholder={field.placeholder || `Enter ${field.label.toLowerCase()}`}
                required={field.required}
              />
            )}
            
            {field.type === 'textarea' && (
              <Textarea
                value={customFieldValues[field.name] || ''}
                onChange={(e) => setCustomFieldValues(prev => ({
                  ...prev,
                  [field.name]: e.target.value
                }))}
                placeholder={field.placeholder || `Enter ${field.label.toLowerCase()}`}
                rows={3}
                required={field.required}
              />
            )}
            
            {field.type === 'number' && (
              <Input
                type="number"
                value={customFieldValues[field.name] || ''}
                onChange={(e) => setCustomFieldValues(prev => ({
                  ...prev,
                  [field.name]: e.target.value
                }))}
                placeholder={field.placeholder || `Enter ${field.label.toLowerCase()}`}
                required={field.required}
              />
            )}
            
            {field.type === 'date' && (
              <Input
                type="date"
                value={customFieldValues[field.name] || ''}
                onChange={(e) => setCustomFieldValues(prev => ({
                  ...prev,
                  [field.name]: e.target.value
                }))}
                required={field.required}
              />
            )}
            
            {field.type === 'time' && (
              <Input
                type="time"
                value={customFieldValues[field.name] || ''}
                onChange={(e) => setCustomFieldValues(prev => ({
                  ...prev,
                  [field.name]: e.target.value
                }))}
                required={field.required}
              />
            )}
            
            {field.type === 'select' && field.options?.length > 0 && (
              <Select
                value={customFieldValues[field.name] || ''}
                onValueChange={(value) => setCustomFieldValues(prev => ({
                  ...prev,
                  [field.name]: value
                }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder={field.placeholder || `Select ${field.label.toLowerCase()}`} />
                </SelectTrigger>
                <SelectContent>
                  {field.options.map((option) => (
                    <SelectItem key={option} value={option}>
                      {option}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            
            {field.type === 'single_select' && field.options?.length > 0 && (
              <Select
                value={customFieldValues[field.name] || ''}
                onValueChange={(value) => setCustomFieldValues(prev => ({
                  ...prev,
                  [field.name]: value
                }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder={field.placeholder || `Select ${field.label.toLowerCase()}`} />
                </SelectTrigger>
                <SelectContent>
                  {field.options.map((option) => (
                    <SelectItem key={option} value={option}>
                      {option}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            
            {field.type === 'multi_select' && field.options?.length > 0 && (
              <div className="space-y-2">
                {field.options.map((option) => (
                  <div key={option} className="flex items-center space-x-2">
                    <Checkbox
                      id={`${field.name}-${option}`}
                      checked={(customFieldValues[field.name] || []).includes(option)}
                      onCheckedChange={(checked) => {
                        const currentValues = customFieldValues[field.name] || [];
                        const newValues = checked
                          ? [...currentValues, option]
                          : currentValues.filter(v => v !== option);
                        setCustomFieldValues(prev => ({
                          ...prev,
                          [field.name]: newValues
                        }));
                      }}
                    />
                    <label htmlFor={`${field.name}-${option}`} className="text-sm">
                      {option}
                    </label>
                  </div>
                ))}
              </div>
            )}
            
            {field.type === 'checkbox' && (
              <div className="flex items-center space-x-2">
                <Checkbox
                  id={field.name}
                  checked={customFieldValues[field.name] || false}
                  onCheckedChange={(checked) => setCustomFieldValues(prev => ({
                    ...prev,
                    [field.name]: checked
                  }))}
                />
                <label htmlFor={field.name} className="text-sm text-muted-foreground">
                  {field.placeholder || `Check to enable ${field.label.toLowerCase()}`}
                </label>
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  return (
    <Layout>
      <div className="max-w-2xl mx-auto space-y-6 sm:space-y-8" data-testid="create-ticket-page">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight" data-testid="create-ticket-title">
            Create New {isITModule ? 'IT' : ''} Ticket
            <span className={`ml-2 sm:ml-3 inline-block text-xs px-2 py-1 rounded-full align-middle font-medium ${isITModule ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300' : 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300'}`}>
              {isITModule ? 'IT Department' : 'Admin Department'}
            </span>
          </h1>
          <p className="text-sm sm:text-base text-muted-foreground mt-1">
            Submit a request to the admin team
          </p>
        </div>

        {/* Rating Blocker - shown when user has unrated completed tickets */}
        {showRatingBlocker && unratedTickets.length > 0 && (
          <Card className="border-orange-300 dark:border-orange-700 bg-orange-50 dark:bg-orange-950/20" data-testid="rating-blocker">
            <CardHeader>
              <CardTitle className="text-orange-700 dark:text-orange-400 flex items-center gap-2 text-lg">
                <Star className="w-5 h-5" />
                Rating Required
              </CardTitle>
              <CardDescription className="text-orange-600 dark:text-orange-400">
                Please rate your completed ticket(s) before raising a new request.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {unratedTickets.map((t) => (
                  <div key={t.ticket_id} className="flex items-center justify-between bg-white dark:bg-background rounded-md px-3 py-2 border">
                    <div>
                      <span className="font-medium text-sm">{t.ticket_id}</span>
                      <span className="text-muted-foreground text-sm ml-2">— {t.request_type}</span>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => navigate(`/tickets/${t.ticket_id}`)}
                      data-testid={`rate-ticket-${t.ticket_id}`}
                    >
                      <Star className="w-3.5 h-3.5 mr-1" /> Rate Now
                    </Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Hide form when rating blocker is active */}
        {!(showRatingBlocker && unratedTickets.length > 0) && (
        <Card className="animate-fade-in">
          <CardHeader>
            <CardTitle>Request Details</CardTitle>
            <CardDescription>
              Fill in the details of your request. Fields marked with * are required.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Request Type — IT module: hidden (fixed to 'IT Request'); Admin module: full picker */}
              {isITModule ? (
                <div className="space-y-2 form-field" data-testid="it-request-type-fixed">
                  <Label>Request Type</Label>
                  <div className="border rounded-md px-3 py-2 text-sm bg-muted/40 text-muted-foreground">
                    IT Request <span className="text-xs">(automatically set — IT team will categorize on resolution)</span>
                  </div>
                </div>
              ) : (
                <div className="space-y-2 form-field">
                  <Label htmlFor="request_type">Request Type *</Label>
                  <Select
                    value={formData.request_type}
                    onValueChange={handleRequestTypeChange}
                    disabled={!!lockedType}
                  >
                    <SelectTrigger id="request_type" data-testid="request-type-select" className={lockedType ? 'opacity-60 cursor-not-allowed' : ''}>
                      <SelectValue placeholder="Select request type" />
                    </SelectTrigger>
                    <SelectContent>
                      {visibleDefaultTypes.map((type) => (
                        <SelectItem key={type} value={type}>
                          {type}
                        </SelectItem>
                      ))}
                      
                      {customRequestTypes
                        .filter(type => !visibleDefaultTypes.includes(type.name))
                        .map((type) => (
                        <SelectItem key={type.id || type.type_id || type.name} value={type.name}>
                          {type.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {lockedType && (
                    <p className="text-xs text-muted-foreground">Request type is locked. Use the dedicated form for other request types.</p>
                  )}
                </div>
              )}

              {/* Location selector — visible only for IT roles (IT Team / IT Head) and Super Admin.
                  Other roles always raise tickets for their own profile location.
                  Hidden in Book Room flow (lockedType) because room booking uses its own
                  room-location selector (restricted to locations that have meeting rooms). */}
              {!lockedType && ['IT Team', 'IT Head', 'Super Admin'].includes(userRole) && !['Client Factory Visit', 'Guest Arrival'].includes(formData.request_type) && (
                <div className="space-y-2 form-field" data-testid="it-location-field">
                  <Label htmlFor="ticket_location">Raise Ticket For Location *</Label>
                  <Select
                    value={formData.ticket_location || employee?.location || ''}
                    onValueChange={(v) => setFormData(prev => ({ ...prev, ticket_location: v }))}
                  >
                    <SelectTrigger id="ticket_location" data-testid="it-location-select">
                      <SelectValue placeholder="Select location" />
                    </SelectTrigger>
                    <SelectContent>
                      {(allLocations.length
                        ? allLocations
                        : ((employee?.location_access?.length ? employee.location_access : [employee?.location]).filter(Boolean))
                      ).map((loc) => (
                        <SelectItem key={loc} value={loc}>{loc}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">Defaults to your profile location. Use this to raise a ticket on behalf of another site.</p>
                </div>
              )}

              {/* Location is auto-captured from employee profile on the backend for non-IT roles */}

              {/* Dynamic Fields based on Request Type — skip entirely for IT (form is Description + Priority + Attachment only) */}
              {formData.request_type && !isITModule && (
                <div className="space-y-4 p-4 bg-muted/30 rounded-lg animate-fade-in">
                  <p className="text-sm font-medium text-muted-foreground">
                    {formData.request_type} Details
                  </p>
                  {formData.request_type === 'Client Factory Visit'
                    ? renderClientFactoryVisit()
                    : (selectedCustomType ? renderCustomFields() : renderDynamicFields())}
                </div>
              )}

              {/* Description - Hidden for Stationery Request and Client Factory Visit */}
              {formData.request_type !== 'Stationery Request' && formData.request_type !== 'Client Factory Visit' && (
                <div className="space-y-2 form-field">
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Provide additional details about your request..."
                    rows={4}
                    data-testid="description-textarea"
                  />
                </div>
              )}

              {/* Priority - Hidden for Stationery Request and Client Factory Visit */}
              {formData.request_type !== 'Stationery Request' && formData.request_type !== 'Client Factory Visit' && (
                <div className="space-y-2 form-field">
                  <Label htmlFor="priority">Priority *</Label>
                  <Select
                    value={formData.priority}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, priority: value }))}
                  >
                    <SelectTrigger id="priority" data-testid="priority-select">
                      <SelectValue placeholder="Select priority" />
                    </SelectTrigger>
                    <SelectContent>
                      {PRIORITIES.map((priority) => (
                        <SelectItem key={priority} value={priority}>
                          {priority}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Attachments */}
              <div className="space-y-2">
                <Label>Attachments (Optional)</Label>
                <div
                  className="drop-zone rounded-lg p-6 text-center cursor-pointer"
                  onClick={() => document.getElementById('file-upload').click()}
                  onDragOver={(e) => {
                    e.preventDefault();
                    e.currentTarget.classList.add('dragging');
                  }}
                  onDragLeave={(e) => {
                    e.currentTarget.classList.remove('dragging');
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    e.currentTarget.classList.remove('dragging');
                    handleFileUpload(Array.from(e.dataTransfer.files));
                  }}
                >
                  <input
                    id="file-upload"
                    type="file"
                    multiple
                    className="hidden"
                    onChange={(e) => handleFileUpload(Array.from(e.target.files))}
                    data-testid="file-upload-input"
                  />
                  {uploading ? (
                    <Loader2 className="w-8 h-8 mx-auto animate-spin text-muted-foreground" />
                  ) : (
                    <>
                      <Upload className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
                      <p className="text-sm text-muted-foreground">
                        Click or drag files to upload
                      </p>
                    </>
                  )}
                </div>

                {/* Uploaded files */}
                {formData.attachments.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-3">
                    {formData.attachments.map((url, index) => (
                      <div
                        key={index}
                        className="flex items-center gap-2 bg-muted px-3 py-1.5 rounded-md text-sm"
                      >
                        <span className="truncate max-w-[150px]">
                          {url.split('/').pop()}
                        </span>
                        <button
                          type="button"
                          onClick={() => removeAttachment(index)}
                          className="text-muted-foreground hover:text-destructive"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Submit Button */}
              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate(-1)}
                  disabled={loading}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={(e) => handleSubmit(e, true)}
                  disabled={loading || !formData.request_type}
                  className="gap-2"
                  data-testid="save-draft-btn"
                >
                  {loading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4" />
                  )}
                  Save as Draft
                </Button>
                <Button
                  type="submit"
                  disabled={loading}
                  className="flex-1 gap-2 btn-press"
                  data-testid="submit-ticket-btn"
                >
                  {loading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                  Submit Request
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
        )}
      </div>
    </Layout>
  );
}
